/**
 * Governed Agent Orchestration Node Type Definitions
 * Defines all node types for the Governed Agent Orchestration canvas.
 * Each node type includes metadata for rendering, validation, and execution.
 */

// ============================================================================
// NODE CATEGORIES
// ============================================================================

/* RC-97j / RC-97k — Unified taxonomy.
 *
 * Single source of truth for the left palette (Add) AND the right
 * Change-Type picker. Each node has a `category` field referencing
 * one of the IDs below; both surfaces filter on that field.
 *
 * IDs are STABLE for back-compat (existing canvases and any external
 * code reading `category === 'enforcement'` keep working). Labels can
 * be retitled freely. Two relabels in RC-97k:
 *   gates       → "Human Input & Approval"  (was "Security Gates" —
 *                  most weren't security-specific; renamed for clarity)
 *   enforcement → "Platform Governance"     (was "Policy & Data
 *                  Protection" — broader name matches actual content)
 *
 * Three NEW categories in RC-97k to close the "Change-Type exposes
 * 28 governance/AI/RAG/External types that operators can't ADD from
 * the left palette" gap:
 *   ai_rag      — LLM, embeddings, vector store, chunk, parse_document,
 *                  document_upload, rule_task
 *   wait_signal — delay, signal_wait, timer aliases (currently scattered
 *                  in 'logic' or 'tasks')
 *   external    — execute_external, external_workflow (currently in
 *                  'enforcement' which is wrong on its face)
 *
 * For the finer-grained sub-groups the right picker uses (e.g.
 * "Messaging & events", "Database, files & secrets") see TYPE_GROUPS
 * below — those are picker-only sub-buckets, not categories. */
export const NODE_CATEGORIES = [
  {
    id: 'triggers',
    label: 'Triggers & Events',
    icon: 'Zap',
    color: '#d97706',
    description: 'Event sources that initiate workflows',
  },
  {
    id: 'tasks',
    label: 'Tasks & Actions',
    icon: 'PlayCircle',
    color: '#0d9488',
    description: 'Core execution: API calls, scripts, notifications, messaging, file/database ops',
  },
  {
    id: 'ai_rag',
    label: 'AI / RAG',
    icon: 'Sparkles',
    color: '#7c3aed',
    description: 'LLM inference, embeddings, vector search, document parsing — the RAG primitives',
  },
  {
    id: 'security_ops',
    label: 'Security Operations',
    icon: 'Shield',
    color: '#ef4444',
    description: 'Specialized security actions: threat intel, containment, forensics',
  },
  {
    id: 'logic',
    label: 'Logic & Control Flow',
    icon: 'GitBranch',
    color: '#8b5cf6',
    description: 'Branching, loops, parallel execution, sub-workflows, error handling',
  },
  {
    id: 'gates',
    label: 'Human Input & Approval',
    icon: 'Lock',
    color: '#f97316',
    description: 'Mid-flow human approval, dual-approval gates, RBAC checks, risk scoring, time windows',
  },
  {
    id: 'data',
    label: 'Data Operations',
    icon: 'Database',
    color: '#06b6d4',
    description: 'Variables, transforms, enrichment, aggregation',
  },
  {
    id: 'wait_signal',
    label: 'Wait & Signal',
    icon: 'Clock',
    color: '#0891b2',
    description: 'Delays, timers, signal-wait pauses for external events',
  },
  {
    id: 'agents',
    label: 'Agents & Skills',
    icon: 'Bot',
    color: '#78716c',
    description: 'Invoke deployed agents and individual skills',
  },
  {
    id: 'enforcement',
    label: 'Platform Protection',
    icon: 'ShieldCheck',
    color: '#dc2626',
    description: 'DPG scans, policy enforcement, RBAC, risk, vault/tokenize, audit trail, compliance scoring, mapper passes — the full protection plane',
  },
  {
    id: 'external',
    label: 'External Workflow',
    icon: 'ExternalLink',
    color: '#9333ea',
    description: 'Execute workflows in external orchestrators (Temporal, Conductor, Airflow, etc.)',
  },
  {
    id: 'connector',
    label: 'Connectors',
    icon: 'Plug',
    color: '#ec4899',
    description: 'Pre-built connector actions for external services',
  },
];

// ============================================================================
// RC-100 — PICKER_BUCKETS (Change-Type picker taxonomy, AUTO-DERIVED)
// ============================================================================
//
// Two surfaces consume the platform taxonomy:
//
//   1. LEFT PALETTE (Add) — uses NODE_CATEGORIES above (12 top-level
//      buckets). Operator-facing: coarse, easy to scan when authoring.
//
//   2. RIGHT CHANGE-TYPE PICKER — uses TYPE_GROUPS (13 finer sub-
//      buckets). Operator changes an existing node's type, so finer
//      granularity helps navigate. The `tasks` category in particular
//      fans out into 3 picker buckets (work units / messaging / files
//      & secrets) because flat-tasks is too coarse to scan.
//
// **Why this changed (RC-100):** RC-97j claimed "single source of
// truth" but TYPE_GROUPS was still a separately-maintained list —
// it drifted from NODE_TYPES (missing dual_approval / rbac_gate /
// risk_score_gate / time_window_gate from Human Input & Approval AND
// advertising a phantom `form_task` that never existed in NODE_TYPES).
// Operators saw 6 gates in the palette but only 2 in the picker.
//
// **This time it's structural.** `PICKER_BUCKETS` below declares the
// 13 buckets with their routing rules (`categories` and/or `typeIds`).
// `TYPE_GROUPS` (exported further down, after NODE_TYPES is in scope)
// is BUILT from NODE_TYPES by iterating every node and routing it
// into exactly one bucket via those rules. A validator throws at
// module init if any node is orphaned (zero buckets matched) or
// double-routed (>1 bucket matched), so drift is impossible: add a
// node with a new category, the build will fail loudly until you
// either widen a bucket's `categories` or add a typeIds override.
//
// IMPORTANT distinction for "user input" questions (preserved
// verbatim from the original Bug #173 note):
//   • To collect input AT WORKFLOW START (Execute-time modal): swap
//     the START/TRIGGER node to manual_start and declare
//     config.inputParameters: ["fieldA", "fieldB"]. Downstream nodes
//     reference ${workflow.input.fieldA}.
//   • To collect input MID-FLOW (operator fills a form during a
//     paused human step in the Approval Center): use human_task with
//     a formSchema config.
//
// Alias handling: nodes carrying an `aliasOf` field on their
// NODE_TYPES entry are flagged isAlias in the derived output so the
// consumer (NodeTypeSwapControl) can hide them from the picker — they
// remain in BE allowedTypes for back-compat with saved canvases.
//
// Routing precedence in buildPickerGroups: explicit `typeIds` win
// over `categories`. Use typeIds when ONE NODE_CATEGORY needs to
// split across multiple picker buckets (currently only `tasks`).

const PICKER_BUCKETS = [
  {
    id: 'triggers',
    label: 'Triggers (workflow entry)',
    hint: 'Pick one as the first node. To prompt the user for input at Execute time, choose manual_start and set config.inputParameters.',
    categories: ['triggers'],
  },
  {
    id: 'human-input',
    label: 'Human Input & Approval',
    hint: 'Mid-flow pauses where an operator fills in or approves data (Approval Center). NOT for start-of-workflow input — for that, use a Trigger.',
    categories: ['gates'],
  },
  {
    id: 'tasks-work',
    label: 'Tasks (work units)',
    hint: 'Generic compute / call-out building blocks.',
    // The `tasks` category splits across multiple picker buckets — explicit
    // typeIds disambiguate which node lands where. Auditing NODE_TYPES
    // (RC-100) found only ~5 real tasks-category nodes belong here;
    // the pre-RC-100 list included 8 phantoms that never existed in
    // NODE_TYPES (rpc_task, webhook_callback_task, task, etc.).
    typeIds: [
      'http_task', 'worker_task', 'system_task', 'script_task', 'http_poll_task',
      // RC-105 — rpc_task lives here (work-unit category) since it's a
      // remote-call primitive analogous to http_task, not a data/file/secret op.
      'rpc_task',
    ],
  },
  {
    id: 'data-ops',
    label: 'Data Operations',
    hint: 'Reshape, filter, validate, enrich, aggregate, set variables.',
    categories: ['data'],
  },
  {
    id: 'logic-control',
    label: 'Logic & Control Flow',
    hint: 'Branches, switches, parallel fork/join, loops, sub-workflows, compensation.',
    categories: ['logic'],
  },
  {
    id: 'wait-signal',
    label: 'Wait & Signal',
    hint: 'Delays, timers, signal-wait pauses for external events.',
    categories: ['wait_signal'],
  },
  {
    id: 'messaging-events',
    label: 'Messaging & Events',
    hint: 'Notifications, email, chat / paging / ITSM integrations, queue consume.',
    // RC-102a + RC-106 — populated with real NODE_TYPES entries now that
    // their schemas exist. BE handlers were already wired (executeEmailTask
    // for email_*, executeConnectorTask + _action suffix strip for
    // slack/teams/pagerduty/jira/servicenow_action, executeConnectorTask
    // for queue-type connectors for message_consume_task). RC-106 added
    // event_publish (pub-sub) + webhook_callback_task (emit half — compose
    // signal_wait downstream for the wait half).
    typeIds: [
      'notification_task',
      'email_task', 'email_send', 'email_smtp',
      'slack_action', 'teams_action', 'pagerduty_action',
      'jira_action', 'servicenow_action',
      'message_consume_task',
      'event_publish', 'webhook_callback_task',
    ],
  },
  {
    id: 'database-files-secrets',
    label: 'Database, Files & Secrets',
    hint: 'SQL/NoSQL, file IO via storage connector, vault:// secret resolution.',
    // RC-104 + RC-105 — populated with real NODE_TYPES entries now that
    // their schemas + BE handlers exist. file_read/write/list route through
    // executeConnectorTask against any storage connector; secret_read/write
    // route through executeConnectorTask against any vault-backed connector;
    // database_task (RC-105) routes through executeConnectorTask against
    // any DB connector with mode-discriminated action (query vs mutate).
    typeIds: [
      'file_list_task', 'file_read_task', 'file_write_task',
      'secret_read_task', 'secret_write_task',
      'database_task',
    ],
  },
  {
    id: 'ai-rag',
    label: 'AI / RAG',
    hint: 'LLM inference, embeddings, vector store + search, document parsing/chunking.',
    categories: ['ai_rag'],
  },
  {
    id: 'agents-skills',
    label: 'Agents, Skills & Connectors',
    hint: 'Invoke a deployed agent, a registry skill, or a connector action directly.',
    // agent_task / skill_task / connector_task are stored under
    // category=tasks in NODE_TYPES (legacy categorization quirk —
    // they're functionally agent-plane invocations). Explicit typeIds
    // pull them into the right picker bucket without touching their
    // category (which would shift them in the LEFT palette too).
    categories: ['agents', 'connector'],
    typeIds: ['agent_task', 'skill_task', 'connector_task'],
  },
  {
    id: 'security-ops',
    label: 'Security Operations',
    hint: 'Threat intel, IOC handling, containment, forensics, vulnerability scan.',
    categories: ['security_ops'],
  },
  {
    id: 'platform-gov',
    label: 'Platform Protection',
    hint: 'DPG scans, policy rule evaluation, enforcement gates, vault tokenize/resolve, audit, compliance scoring.',
    categories: ['enforcement'],
  },
  {
    id: 'external-wf',
    label: 'External Workflow',
    hint: 'Execute a workflow on Temporal / Conductor / Argo / Airflow / Step Functions via WorkflowConnector.',
    categories: ['external'],
  },
];
// TYPE_GROUPS export lives near the bottom of this file — it needs
// NODE_TYPES to be in scope before it can iterate.

// ============================================================================
// TRIGGER NODE TYPES (5)
// ============================================================================

const TRIGGER_NODES = {
  manual_start: {
    type: 'manual_start',
    label: 'Manual Start',
    category: 'triggers',
    icon: 'Play',
    description: 'Manually trigger workflow execution',
    color: '#d97706',
    /* Phase B-fix.3 — hidden from palette. manual_start is the canonical
     * entry sentinel auto-seeded by makeFreshSentinelPayload (CanvasPageV3:481);
     * exactly one per workflow, never operator-added. SINGLE_INSTANCE_ENTRY_TYPES
     * still refuses duplicates as defense in depth, but hiding from the palette
     * removes the failure mode entirely. See SYSTEM_WORKFLOW_RULES.md §2.6. */
    paletteHidden: true,
    ports: {
      in: [],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'workflowName',
        label: 'Workflow Name',
        type: 'text',
        required: false,
        placeholder: 'Optional workflow identifier',
        helpText: 'Display name for this workflow trigger',
      },
    ],
    outputSchema: {
      timestamp: 'ISO8601',
      triggeredBy: 'string',
      triggerType: 'string',
    },
    tags: ['entry-point', 'manual'],
  },

  webhook: {
    type: 'webhook',
    label: 'Webhook',
    category: 'triggers',
    icon: 'Zap',
    description: 'HTTP webhook to trigger workflow from external systems',
    color: '#d97706',
    ports: {
      in: [],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'path',
        label: 'Webhook Path',
        type: 'text',
        required: true,
        placeholder: '/webhooks/security-alert',
        helpText: 'URL path for this webhook endpoint',
      },
      {
        field: 'method',
        label: 'HTTP Method',
        type: 'select',
        required: true,
        options: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'],
        default: 'POST',
        helpText: 'HTTP method to accept',
      },
      {
        field: 'headers',
        label: 'Expected Headers',
        type: 'json',
        required: false,
        placeholder: '{"x-api-key": "required"}',
        helpText: 'JSON object of expected request headers for validation',
      },
      {
        field: 'authRequired',
        label: 'Require API Key Auth',
        type: 'boolean',
        required: false,
        default: true,
        helpText: 'Require API key authentication for webhook calls',
      },
    ],
    outputSchema: {
      body: 'object',
      headers: 'object',
      query: 'object',
      timestamp: 'ISO8601',
    },
    tags: ['connector', 'async', 'external'],
  },

  schedule: {
    type: 'schedule',
    label: 'Schedule',
    category: 'triggers',
    icon: 'Clock',
    description: 'Trigger workflow on a schedule (cron)',
    color: '#d97706',
    ports: {
      in: [],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'cron',
        label: 'Cron Expression',
        type: 'text',
        required: true,
        placeholder: '0 9 * * 1-5',
        helpText: 'Cron expression (minute hour day month dayOfWeek)',
      },
      {
        field: 'timezone',
        label: 'Timezone',
        type: 'select',
        required: true,
        options: [
          'UTC',
          'America/New_York',
          'America/Chicago',
          'America/Denver',
          'America/Los_Angeles',
          'Europe/London',
          'Europe/Paris',
          'Asia/Tokyo',
          'Asia/Shanghai',
          'Australia/Sydney',
        ],
        default: 'UTC',
        helpText: 'Timezone for cron evaluation',
      },
      {
        field: 'description',
        label: 'Description',
        type: 'text',
        required: false,
        placeholder: 'Daily security scan at 9 AM',
        helpText: 'Human-readable description of schedule',
      },
    ],
    outputSchema: {
      timestamp: 'ISO8601',
      executionNumber: 'integer',
      nextExecutionTime: 'ISO8601',
    },
    tags: ['scheduled', 'periodic', 'automation'],
  },

  siem_alert: {
    type: 'siem_alert',
    label: 'SIEM Alert',
    category: 'triggers',
    icon: 'AlertTriangle',
    description: 'Ingest alerts from SIEM/monitoring systems',
    color: '#d97706',
    ports: {
      in: [],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'alertSource',
        label: 'Alert Source',
        type: 'select',
        required: true,
        options: ['splunk', 'elastic', 'datadog', 'sumo_logic', 'cloudwatch', 'sentinel', 'generic'],
        helpText: 'SIEM system providing alerts',
      },
      {
        field: 'severityFilter',
        label: 'Minimum Severity',
        type: 'select',
        required: false,
        options: ['critical', 'high', 'medium', 'low', 'info'],
        default: 'medium',
        helpText: 'Only trigger on alerts with this severity or higher',
      },
      {
        field: 'alertType',
        label: 'Alert Type Filter',
        type: 'text',
        required: false,
        placeholder: 'malware,phishing,intrusion',
        helpText: 'Comma-separated list of alert types to trigger on',
      },
      {
        field: 'sourceConfig',
        label: 'Source Configuration',
        type: 'json',
        required: false,
        placeholder: '{"apiKey": "***", "endpoint": "..."}',
        helpText: 'Connection details for SIEM system',
      },
    ],
    outputSchema: {
      alertId: 'string',
      severity: 'string',
      type: 'string',
      source: 'string',
      alert: 'object',
      timestamp: 'ISO8601',
    },
    tags: ['security', 'monitoring', 'alert'],
  },

  email_trigger: {
    type: 'email_trigger',
    label: 'Email Trigger',
    category: 'triggers',
    icon: 'Mail',
    description: 'Trigger workflow from email events',
    color: '#d97706',
    ports: {
      in: [],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'mailbox',
        label: 'Email Mailbox',
        type: 'text',
        required: true,
        placeholder: 'security-alerts@company.com',
        helpText: 'Email address or mailbox to monitor',
      },
      {
        field: 'subjectFilter',
        label: 'Subject Filter',
        type: 'text',
        required: false,
        placeholder: 'ALERT|INCIDENT|SECURITY',
        helpText: 'Regex or keywords to filter emails by subject',
      },
      {
        field: 'senderFilter',
        label: 'Sender Filter',
        type: 'text',
        required: false,
        placeholder: '@company.com|@trusted-vendor.com',
        helpText: 'Regex or domains to filter emails by sender',
      },
      {
        field: 'includeAttachments',
        label: 'Include Attachments',
        type: 'boolean',
        required: false,
        default: false,
        helpText: 'Extract and parse email attachments',
      },
    ],
    outputSchema: {
      subject: 'string',
      from: 'string',
      to: 'string',
      body: 'string',
      attachments: 'array',
      timestamp: 'ISO8601',
      messageId: 'string',
    },
    tags: ['email', 'connector', 'alert'],
  },

  system_event: {
    type: 'system_event',
    label: 'System Event Trigger',
    category: 'triggers',
    icon: 'Zap',
    description:
      'Platform-level trigger for system workflows — fires on policy violations, health alerts, ' +
      'approval lifecycle events, scheduled digests, and enforcement pipeline invocations.',
    color: '#d97706',
    ports: {
      in: [],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'workflowType',
        label: 'Workflow Type',
        type: 'text',
        required: true,
        placeholder: 'e.g. pii_scan, workflow_entry_gate, compliance_scoring',
        helpText: 'Identifier for the system event that triggers this workflow',
      },
      {
        field: 'outputVariable',
        label: 'Output Variable',
        type: 'text',
        required: false,
        default: 'trigger-1_output',
        helpText: 'Variable name for downstream node input mappings',
      },
    ],
    outputSchema: {
      workflowType: 'string',
      triggerType: 'string',
      triggeredAt: 'ISO8601',
      tenantId: 'string',
      orgId: 'string',
      agentId: 'string',
      userId: 'string',
      workflowExecutionId: 'string',
    },
    tags: ['system', 'platform', 'enforcement', 'trigger'],
  },
};

// ============================================================================
// TASK NODE TYPES (8)
// ============================================================================

const TASK_NODES = {
  http_task: {
    type: 'http_task',
    label: 'HTTP Request',
    category: 'tasks',
    icon: 'Globe',
    description: 'Execute REST API call',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'url',
        label: 'URL',
        type: 'text',
        required: true,
        placeholder: 'https://api.example.com/v1/endpoint',
        helpText: 'Full URL or template with variables',
      },
      {
        field: 'method',
        label: 'HTTP Method',
        type: 'select',
        required: true,
        options: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'],
        default: 'POST',
        helpText: 'HTTP method for request',
      },
      {
        field: 'headers',
        label: 'Headers',
        type: 'json',
        required: false,
        placeholder: '{"Content-Type": "application/json"}',
        helpText: 'HTTP headers as JSON object',
      },
      {
        field: 'body',
        label: 'Request Body',
        type: 'json',
        required: false,
        placeholder: '{"key": "value"}',
        helpText: 'JSON body or template',
      },
      {
        field: 'auth',
        label: 'Authentication',
        type: 'select',
        required: false,
        options: ['none', 'basic', 'bearer', 'api_key', 'oauth2'],
        default: 'none',
        helpText: 'Authentication method',
      },
      {
        field: 'authConfig',
        label: 'Auth Configuration',
        type: 'json',
        required: false,
        placeholder: '{"token": "***"}',
        helpText: 'Auth details (credentials stored securely)',
      },
      {
        field: 'retryCount',
        label: 'Retry Count',
        type: 'number',
        required: false,
        default: 3,
        helpText: 'Number of retries on failure',
      },
      {
        field: 'retryDelay',
        label: 'Retry Delay (ms)',
        type: 'number',
        required: false,
        default: 1000,
        helpText: 'Delay between retries',
      },
      {
        field: 'timeout',
        label: 'Timeout (seconds)',
        type: 'number',
        required: false,
        default: 30,
        helpText: 'Request timeout in seconds',
      },
    ],
    outputSchema: {
      statusCode: 'integer',
      headers: 'object',
      body: 'object|string',
      duration: 'number',
      success: 'boolean',
    },
    tags: ['connector', 'api', 'external'],
  },

  // ==========================================================================
  // RC-105 — database_task + rpc_task (mirror http_task pattern, connector-mediated)
  // ==========================================================================
  // CanonicalNodeType enum entries (DATABASE_QUERY, DATABASE_MUTATION, RPC_CALL)
  // were already in the IR. BE case branches added in CanvasDAGWorkflowImpl.
  // Both route through executeConnectorTask — DB/RPC need credentials and
  // service definitions, which is the connector contract.

  database_task: {
    type: 'database_task',
    label: 'Database Query',
    category: 'tasks',
    icon: 'Database',
    description: 'Run a parameterised SQL/NoSQL query or mutation against a database connector (Postgres, MySQL, Mongo, DynamoDB, BigQuery, Snowflake, etc.). Use mode=query for reads, mode=mutate for writes.',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connectorId',
        label: 'Database Connector',
        type: 'text',
        required: true,
        placeholder: 'UUID of a configured database connector',
        helpText: 'Configured at Admin > Connectors. Connector type can be postgres, mysql, mongodb, dynamodb, bigquery, snowflake, or a custom JDBC connector.',
      },
      {
        field: 'mode',
        label: 'Mode',
        type: 'select',
        required: true,
        default: 'query',
        options: [
          { value: 'query', label: 'Query (read — SELECT or equivalent)' },
          { value: 'mutate', label: 'Mutate (write — INSERT / UPDATE / DELETE / upsert)' },
        ],
        helpText: 'BE routes query → connector.read action, mutate → connector.write action.',
      },
      {
        field: 'statement',
        label: 'SQL / Query Statement',
        type: 'textarea',
        required: true,
        placeholder: 'SELECT id, email FROM users WHERE tenant_id = :tenantId AND active = :active',
        helpText: 'Parameterised statement. Use :namedParam placeholders (NEVER string-concatenate user input — SQL injection risk). For NoSQL, this is the equivalent query DSL (Mongo aggregation pipeline, Dynamo expression, etc.).',
      },
      {
        field: 'parameters',
        label: 'Parameters (JSON)',
        type: 'json',
        required: false,
        placeholder: '{ "tenantId": "${trigger-1_output.tenantId}", "active": true }',
        helpText: 'Map of named parameter values. Supports ${...} variable references — these are bound parameter-style, not interpolated into the statement string.',
      },
      {
        field: 'resultLimit',
        label: 'Result Limit',
        type: 'number',
        required: false,
        default: 1000,
        helpText: 'Max rows returned for query mode. Prevents unbounded result sets from OOMing the worker. Ignored for mutate mode.',
      },
      {
        field: 'timeout',
        label: 'Statement Timeout (seconds)',
        type: 'number',
        required: false,
        default: 30,
        helpText: 'Aborts the statement after N seconds. Use a low value for OLTP queries to protect the DB from runaway scans.',
      },
      {
        field: 'transactionScope',
        label: 'Transaction Scope',
        type: 'select',
        required: false,
        default: 'auto',
        options: [
          { value: 'auto', label: 'Auto (single statement = single transaction)' },
          { value: 'workflow', label: 'Workflow (join the workflow-scoped transaction if connector supports it)' },
        ],
        helpText: 'workflow scope is only honoured by connectors that opt in to distributed transactions — most JDBC connectors are auto-only.',
      },
    ],
    outputSchema: {
      rows: 'array',          // query mode
      rowCount: 'integer',
      affectedRows: 'integer',// mutate mode
      lastInsertId: 'string', // mutate mode (where supported)
      durationMs: 'number',
      success: 'boolean',
    },
    tags: ['database', 'sql', 'jdbc', 'postgres', 'mysql', 'mongodb', 'query'],
  },

  rpc_task: {
    type: 'rpc_task',
    label: 'RPC Call',
    category: 'tasks',
    icon: 'Radio',
    description: 'Invoke a remote gRPC or JSON-RPC method via an RPC connector. Connector handles transport (HTTP/2 framing for gRPC, POST+JSON for JSON-RPC), service definition, and credentials.',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connectorId',
        label: 'RPC Connector',
        type: 'text',
        required: true,
        placeholder: 'UUID of a configured RPC connector',
        helpText: 'Connector encapsulates the target service endpoint, the .proto definition (gRPC) or schema (JSON-RPC), and the credentials. Type can be grpc, json_rpc, or a custom RPC connector.',
      },
      {
        field: 'service',
        label: 'Service Name',
        type: 'text',
        required: false,
        placeholder: 'com.example.UserService',
        helpText: 'For gRPC: fully-qualified service name from the .proto. For JSON-RPC: the namespace prefix if your endpoint groups methods. Optional when the connector pins a single service.',
      },
      {
        field: 'method',
        label: 'Method Name',
        type: 'text',
        required: true,
        placeholder: 'GetUser, ListInvoices, eth_blockNumber',
        helpText: 'Remote method to invoke. Case-sensitive — match the .proto / schema definition exactly.',
      },
      {
        field: 'requestPayload',
        label: 'Request Payload (JSON)',
        type: 'json',
        required: false,
        placeholder: '{ "userId": "${trigger-1_output.userId}" }',
        helpText: 'Method arguments as a JSON object. Connector marshals into the wire format (protobuf for gRPC, JSON-RPC params array/object for JSON-RPC).',
      },
      {
        field: 'callOptions',
        label: 'Call Options (JSON)',
        type: 'json',
        required: false,
        placeholder: '{ "deadlineMs": 5000, "metadata": { "x-request-id": "${...}" } }',
        helpText: 'gRPC-style call options: deadlineMs, metadata, retryPolicy. JSON-RPC ignores most of these — deadlineMs is honored as a request timeout.',
      },
      {
        field: 'timeout',
        label: 'Timeout (seconds)',
        type: 'number',
        required: false,
        default: 30,
        helpText: 'Hard timeout on the RPC. Overrides callOptions.deadlineMs if both set (whichever is shorter wins).',
      },
      {
        field: 'retryOnFailure',
        label: 'Retry on Failure',
        type: 'boolean',
        required: false,
        default: false,
        helpText: 'When true, transient gRPC errors (UNAVAILABLE, DEADLINE_EXCEEDED) and JSON-RPC server-error responses get up to 3 retries with backoff. Disable for non-idempotent methods.',
      },
    ],
    outputSchema: {
      response: 'object',
      statusCode: 'integer',  // gRPC status code or JSON-RPC error code
      ok: 'boolean',
      durationMs: 'number',
      attempts: 'integer',
    },
    tags: ['rpc', 'grpc', 'json-rpc', 'remote', 'service'],
  },

  // ─── Worker Task (Conductor SimpleTask / customer-written polling worker) ───
  // Hosts customer code that long-polls the orchestrator for work units
  // keyed on `taskDefName` and reports the result back. The full Conductor
  // task-def schema lives here so imported Conductor OSS SIMPLE tasks round-trip
  // losslessly. The `hostingMode` field is the layer-2 toggle that flips
  // between conductor-style polling (default) and Agentlets-managed
  // (Temporal OSS-backed) hosting — gated behind the `workerHosting` feature
  // flag at the panel level until the backend is wired.
  worker_task: {
    type: 'worker_task',
    label: 'Worker Task',
    category: 'tasks',
    icon: 'Cpu',
    description: 'Customer-written worker that polls for work and reports results',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'taskDefName',
        label: 'Task Definition Name',
        type: 'text',
        required: true,
        placeholder: 'findPolicyForCustomer',
        helpText: 'The unique name your worker polls for. Must match the @WorkerTask("…") / @WorkerTask(task_definition_name=…) string in your worker code.',
      },
      {
        field: 'hostingMode',
        label: 'Hosting Mode',
        type: 'select',
        required: false,
        options: [
          { value: 'conductor_poll', label: 'Conductor-style polling (you run the worker)' },
          { value: 'agentlets_managed', label: 'Agentlets-managed (we run it on Temporal OSS)' },
        ],
        default: 'conductor_poll',
        helpText: 'Where the worker runs. Conductor mode is the default and matches Conductor OSS semantics exactly; managed mode hosts the worker on our Temporal OSS-backed runtime (gated behind workerHosting feature flag).',
      },
      {
        field: 'inputKeys',
        label: 'Input Keys',
        type: 'json',
        required: false,
        placeholder: '["customerId", "policyType"]',
        helpText: 'Names of fields the worker expects as input. Drives the @InputParam map in generated stubs.',
      },
      {
        field: 'outputKeys',
        label: 'Output Keys',
        type: 'json',
        required: false,
        placeholder: '["policyNumber", "policyType", "issuedAt"]',
        helpText: 'Names of fields the worker returns. Used by downstream nodes that reference this output.',
      },
      // ── Worker-specific runtime knobs (NOT covered by Attributes) ──
      // Generic retry / execution-timeout policy lives on the Attributes
      // tab — that's the single platform-wide source of truth applied
      // to every task type, so we deliberately don't repeat retryCount /
      // retryLogic / retryDelaySeconds / backoffScaleFactor /
      // timeoutSeconds / timeoutPolicy here. The fields below are only
      // those that make sense exclusively for a customer-written
      // polling worker (response timeout = "how long after CLAIMED
      // before reaper times out the row", poll timeout = "how long the
      // queued task waits for a poller", concurrency / domain /
      // ownerEmail = Conductor task-def metadata Attributes doesn't
      // know about).
      {
        field: 'responseTimeoutSeconds',
        label: 'Response Timeout (s)',
        type: 'number',
        required: false,
        default: 600,
        helpText: 'How long without a status update from the worker before the task is marked TIMED_OUT. Distinct from the Attributes-tab execution timeout — that bounds total work; this bounds silence after a CLAIMED.',
      },
      {
        field: 'pollTimeoutSeconds',
        label: 'Poll Timeout (s)',
        type: 'number',
        required: false,
        default: 3600,
        helpText: 'How long a queued task waits for a worker to pick it up before being abandoned.',
      },
      {
        field: 'concurrentExecLimit',
        label: 'Concurrent Execution Limit',
        type: 'number',
        required: false,
        default: 0,
        helpText: 'Cap on simultaneous executions of this task across all workers (0 = unlimited).',
      },
      {
        field: 'ownerEmail',
        label: 'Owner Email',
        type: 'text',
        required: false,
        placeholder: 'team@company.com',
        helpText: 'Owning team for this task definition. Surfaced in audit/ownership views.',
      },
      {
        field: 'domain',
        label: 'Worker Domain',
        type: 'text',
        required: false,
        placeholder: 'prod / staging / region:us-west-2',
        helpText: 'Optional routing key — only workers polling with this domain will pick up tasks of this definition.',
      },
    ],
    outputSchema: {
      // The actual shape is defined by outputKeys above; these are the
      // Conductor-standard envelope fields that always come back.
      taskId: 'string',
      status: 'string',
      output: 'object',
      worker: 'string',
      durationMs: 'number',
    },
    tags: ['worker', 'polling', 'conductor', 'simpletask'],
  },

  llm_task: {
    type: 'llm_task',
    label: 'LLM / AI Inference',
    category: 'ai_rag',
    icon: 'Brain',
    description: 'Execute LLM/AI model for analysis or generation',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      // RC-8 (task #188) — connectorId picker is the primary config field.
      // Dropdown options are populated dynamically in InputTab from the
      // tenant's installed connectors of LLM type (anthropic, openai,
      // azure_openai, bedrock, gemini, etc.). The runtime routes every
      // LLM call through this connector — no localhost fallback. If the
      // tenant has no LLM connector configured, the picker is empty and
      // the placeholder copy directs the operator to Skill Center →
      // Connectors to install one.
      {
        field: 'connectorId',
        label: 'LLM Connector',
        type: 'select',
        required: true,
        options: [], // populated at render time from installed LLM connectors
        helpText: 'Pick a configured LLM connector. Install one in Skill Center → Connectors if none appear.',
        dynamicOptions: 'llmConnectors',
      },
      {
        field: 'mode',
        label: 'Mode',
        type: 'select',
        required: true,
        options: ['chat', 'text'],
        default: 'chat',
        helpText: 'chat = multi-turn messages; text = single prompt → completion',
      },
      {
        field: 'model',
        label: 'Model',
        type: 'select',
        required: true,
        options: [
          'claude-sonnet-4-6', 'claude-opus-4-6', 'claude-haiku-4-5-20251001',
          'gpt-4o', 'gpt-4-turbo', 'gpt-4', 'gpt-3.5-turbo',
          'gemini-1.5-pro', 'gemini-1.5-flash',
        ],
        default: 'claude-sonnet-4-6',
        helpText: 'LLM model to use (must be supported by the picked connector)',
      },
      {
        field: 'systemPrompt',
        label: 'System Prompt',
        type: 'textarea',
        required: false,
        placeholder: 'You are a security analyst...',
        helpText: 'System message defining model behavior (chat mode only)',
      },
      {
        field: 'userPromptTemplate',
        label: 'User Prompt Template',
        type: 'textarea',
        required: true,
        placeholder: 'Analyze this alert: {{alert}} for {{threatType}}',
        helpText: 'User message template with {{variable}} placeholders',
      },
      {
        field: 'temperature',
        label: 'Temperature',
        type: 'number',
        required: false,
        default: 0.7,
        min: 0,
        max: 2,
        step: 0.1,
        helpText: 'Controls randomness/creativity (0=deterministic, 2=creative)',
      },
      {
        field: 'maxTokens',
        label: 'Max Tokens',
        type: 'number',
        required: false,
        default: 2000,
        helpText: 'Maximum tokens in response',
      },
      {
        field: 'outputFormat',
        label: 'Output Format',
        type: 'select',
        required: false,
        options: ['text', 'json', 'markdown', 'structured'],
        default: 'json',
        helpText: 'Format of LLM response',
      },
      {
        field: 'timeout',
        label: 'Timeout (seconds)',
        type: 'number',
        required: false,
        default: 60,
        helpText: 'Request timeout',
      },
    ],
    outputSchema: {
      response: 'string|object',
      model: 'string',
      tokensUsed: 'integer',
      cost: 'number',
      duration: 'number',
    },
    tags: ['ai', 'analysis', 'generation'],
  },

  system_task: {
    type: 'system_task',
    label: 'System Operation',
    category: 'tasks',
    icon: 'Cog',
    description: 'System operations: variables, transforms, logging, sleep',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'operation',
        label: 'Operation',
        type: 'select',
        required: true,
        options: ['set_variable', 'transform', 'log', 'sleep'],
        helpText: 'Type of system operation',
      },
      {
        field: 'variableName',
        label: 'Variable Name',
        type: 'text',
        required: false,
        placeholder: 'my_variable',
        helpText: 'Required for set_variable operation',
      },
      {
        field: 'variableValue',
        label: 'Variable Value',
        type: 'json',
        required: false,
        placeholder: 'value or {{expression}}',
        helpText: 'Value to set (supports templates)',
      },
      {
        field: 'transformExpression',
        label: 'Transform Expression',
        type: 'text',
        required: false,
        placeholder: 'input.severity === "critical" ? "escalate" : "normal"',
        helpText: 'JavaScript expression for data transformation',
      },
      {
        field: 'logMessage',
        label: 'Log Message',
        type: 'textarea',
        required: false,
        placeholder: 'Processing alert: {{alertId}}',
        helpText: 'Message to log (supports templates)',
      },
      {
        field: 'logLevel',
        label: 'Log Level',
        type: 'select',
        required: false,
        options: ['debug', 'info', 'warn', 'error'],
        default: 'info',
        helpText: 'Logging level',
      },
      {
        field: 'sleepDuration',
        label: 'Sleep Duration (ms)',
        type: 'number',
        required: false,
        placeholder: '5000',
        helpText: 'Duration to sleep in milliseconds',
      },
    ],
    outputSchema: {
      variableName: 'string',
      previousValue: 'any',
      newValue: 'any',
      transformedData: 'object',
      logEntry: 'string',
      duration: 'number',
    },
    tags: ['utility', 'data', 'control'],
  },

  connector_task: {
    type: 'connector_task',
    label: 'Connector Action',
    category: 'tasks',
    icon: 'Plug',
    description: 'Execute pre-built connector action',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connectorId',
        label: 'Connector',
        type: 'select',
        required: true,
        options: [],
        helpText: 'Select a configured connector',
      },
      {
        field: 'action',
        label: 'Action',
        type: 'select',
        required: true,
        options: [],
        helpText: 'Action supported by selected connector',
      },
      {
        field: 'parameters',
        label: 'Action Parameters',
        type: 'json',
        required: false,
        placeholder: '{"param1": "value1"}',
        helpText: 'Parameters required by the action',
      },
    ],
    outputSchema: {
      connectorId: 'string',
      action: 'string',
      result: 'object',
      success: 'boolean',
      error: 'string',
    },
    tags: ['connector', 'external'],
  },

  script_task: {
    type: 'script_task',
    label: 'Execute Script',
    category: 'tasks',
    icon: 'Code',
    description: 'Execute inline code (Python or JavaScript)',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'language',
        label: 'Language',
        type: 'select',
        required: true,
        options: ['javascript', 'python'],
        default: 'javascript',
        helpText: 'Programming language',
      },
      {
        field: 'code',
        label: 'Code',
        type: 'code',
        required: true,
        placeholder: 'return input.severity > 5 ? "high" : "low";',
        helpText: 'Code to execute (input/context available)',
      },
      {
        field: 'timeout',
        label: 'Timeout (seconds)',
        type: 'number',
        required: false,
        default: 30,
        helpText: 'Script execution timeout',
      },
      {
        field: 'sandboxed',
        label: 'Sandboxed Execution',
        type: 'boolean',
        required: false,
        default: true,
        helpText: 'Run in isolated sandbox for security',
      },
    ],
    outputSchema: {
      result: 'any',
      stdout: 'string',
      stderr: 'string',
      duration: 'number',
      success: 'boolean',
    },
    tags: ['code', 'execution', 'custom'],
  },

  notification_task: {
    type: 'notification_task',
    label: 'Send Notification',
    category: 'tasks',
    icon: 'Send',
    description: 'Send notification via email, Slack, Teams, PagerDuty, webhook',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'channel',
        label: 'Channel',
        type: 'select',
        required: true,
        options: ['email', 'slack', 'teams', 'pagerduty', 'webhook'],
        helpText: 'Notification channel',
      },
      {
        field: 'recipients',
        label: 'Recipients',
        type: 'text',
        required: true,
        placeholder: 'user@company.com, #security-team',
        helpText: 'Comma-separated recipients (emails, channels, user IDs)',
      },
      {
        field: 'templateId',
        label: 'Email Template',
        type: 'template_select',
        required: false,
        templateType: 'email',
        helpText: 'Select a template from the Template Center, or leave blank to use custom subject/body below',
      },
      {
        field: 'template',
        label: 'Template Preset',
        type: 'select',
        required: false,
        options: ['alert', 'incident', 'approval_request', 'custom'],
        default: 'custom',
        helpText: 'Quick preset (overridden if Email Template is selected above)',
      },
      {
        field: 'subject',
        label: 'Subject/Title',
        type: 'text',
        required: false,
        placeholder: 'Security Alert: {{severity}} Threat Detected',
        helpText: 'Subject line — auto-filled when a template is selected',
      },
      {
        field: 'body',
        label: 'Message Body',
        type: 'textarea',
        required: false,
        placeholder: 'Alert Details:\n{{alertSummary}}',
        helpText: 'Message body — auto-filled when a template is selected',
      },
      {
        field: 'priority',
        label: 'Priority',
        type: 'select',
        required: false,
        options: ['low', 'medium', 'high', 'critical'],
        default: 'medium',
        helpText: 'Notification priority level',
      },
      {
        field: 'includeAttachments',
        label: 'Include Attachments',
        type: 'boolean',
        required: false,
        default: false,
        helpText: 'Attach relevant data/files',
      },
    ],
    outputSchema: {
      channel: 'string',
      recipients: 'array',
      messageId: 'string',
      sent: 'boolean',
      timestamp: 'ISO8601',
    },
    tags: ['notification', 'communication', 'alert'],
  },

  // ==========================================================================
  // RC-106 — event_publish + webhook_callback_task (connector-mediated)
  // ==========================================================================
  // event_publish: fire-and-forget event emission to a messaging connector
  // (Kafka, RabbitMQ, SNS, EventBridge, internal pub-sub).
  // webhook_callback_task: emit a webhook synchronously; for the WAIT half,
  // compose a signal_wait node downstream (keeps the wait-primitive composable
  // and avoids touching the Temporal signal layer that powers human_task /
  // form_task).
  // Both route through executeConnectorTask (BE case branches in
  // CanvasDAGWorkflowImpl) — same RC-104/105 pattern, zero new infrastructure.

  event_publish: {
    type: 'event_publish',
    label: 'Publish Event',
    category: 'tasks',
    icon: 'Radio',
    description: 'Publish a fire-and-forget event to a messaging bus (Kafka, RabbitMQ, SNS, EventBridge, Pub/Sub, internal). For request/response semantics use rpc_task; for wait-for-reply use webhook_callback_task + signal_wait.',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connectorId',
        label: 'Messaging Connector',
        type: 'text',
        required: true,
        placeholder: 'UUID of a configured messaging connector',
        helpText: 'Configured at Admin > Connectors. Connector type can be kafka, rabbitmq, sns, eventbridge, gcp_pubsub, or a custom messaging connector.',
      },
      {
        field: 'topic',
        label: 'Topic / Channel / ARN',
        type: 'text',
        required: true,
        placeholder: 'security.alerts.critical, arn:aws:sns:us-east-1:...:topic',
        helpText: 'Destination identifier. For Kafka: topic name. For SNS: topic ARN. For RabbitMQ: exchange name. Connector-specific.',
      },
      {
        field: 'eventType',
        label: 'Event Type',
        type: 'text',
        required: false,
        placeholder: 'incident.created, agent.violation',
        helpText: 'Logical event name. Promoted to the message header / attribute so downstream consumers can route without parsing the body.',
      },
      {
        field: 'payload',
        label: 'Event Payload (JSON)',
        type: 'json',
        required: true,
        placeholder: '{ "incidentId": "${trigger-1_output.incidentId}", "severity": "${...}" }',
        helpText: 'Body of the event. Supports ${...} variable references. Serialized as JSON over the wire — connector handles backend-specific framing (Avro, protobuf, raw JSON).',
      },
      {
        field: 'attributes',
        label: 'Attributes / Headers (JSON)',
        type: 'json',
        required: false,
        placeholder: '{ "tenantId": "${...}", "correlationId": "${...}" }',
        helpText: 'Message-level metadata (SNS attributes, Kafka headers, RabbitMQ headers). Useful for filtering / fan-out without payload inspection.',
      },
      {
        field: 'partitionKey',
        label: 'Partition Key',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.tenantId}',
        helpText: 'For partitioned brokers (Kafka, Kinesis). Same key → same partition → ordering preserved per key. Ignored for non-partitioned brokers.',
      },
    ],
    outputSchema: {
      messageId: 'string',
      published: 'boolean',
      topic: 'string',
      partition: 'integer',
      offset: 'integer',
      timestamp: 'ISO8601',
    },
    tags: ['event', 'pub-sub', 'kafka', 'sns', 'rabbitmq', 'messaging'],
  },

  webhook_callback_task: {
    type: 'webhook_callback_task',
    label: 'Emit Webhook (callback-ready)',
    category: 'tasks',
    icon: 'Webhook',
    description: 'Synchronously emit an outbound webhook via a webhook connector. For the wait-for-callback half, place a signal_wait node downstream — keeps emit/wait composable and lets you parallelise the wait if the callback is optional.',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connectorId',
        label: 'Webhook Connector',
        type: 'text',
        required: true,
        placeholder: 'UUID of a configured webhook connector',
        helpText: 'Connector encapsulates the target URL, auth (HMAC / bearer / basic), retry policy, and TLS settings.',
      },
      {
        field: 'urlOverride',
        label: 'URL Override',
        type: 'text',
        required: false,
        placeholder: 'https://partner.example.com/webhooks/${trigger-1_output.id}',
        helpText: 'Optional. When set, overrides the connector\'s default URL — useful when the same webhook connector posts to per-tenant or per-customer URLs.',
      },
      {
        field: 'method',
        label: 'HTTP Method',
        type: 'select',
        required: false,
        default: 'POST',
        options: ['POST', 'PUT', 'PATCH'],
      },
      {
        field: 'payload',
        label: 'Payload (JSON)',
        type: 'json',
        required: true,
        placeholder: '{ "event": "incident.created", "data": { ... }, "callback_signal": "incident_ack_${nodeId}" }',
        helpText: 'Body of the webhook. To enable callback waiting, include a `callback_signal` field with a unique signal name, then place a signal_wait node downstream waiting on that same signal — the callback receiver hits a return endpoint that resolves the signal.',
      },
      {
        field: 'headers',
        label: 'Additional Headers (JSON)',
        type: 'json',
        required: false,
        placeholder: '{ "X-Correlation-Id": "${...}" }',
        helpText: 'Merged with the connector\'s baseline headers (auth lives there). Use for per-event headers.',
      },
      {
        field: 'expectedStatusCodes',
        label: 'Success Status Codes (comma-separated)',
        type: 'text',
        required: false,
        default: '200,201,202,204',
        helpText: 'Status codes treated as success. Anything else triggers the connector\'s retry policy (configured at the connector level).',
      },
      {
        field: 'timeout',
        label: 'Emit Timeout (seconds)',
        type: 'number',
        required: false,
        default: 30,
        helpText: 'Timeout on the POST itself. NOT the wait-for-callback window — for that, set the timeout on the downstream signal_wait node.',
      },
    ],
    outputSchema: {
      statusCode: 'integer',
      responseBody: 'object|string',
      callbackSignal: 'string', // echoed from payload if present, for downstream signal_wait wiring
      emittedAt: 'ISO8601',
      attempts: 'integer',
      success: 'boolean',
    },
    tags: ['webhook', 'callback', 'emit', 'compound', 'event'],
  },

  agent_task: {
    type: 'agent_task',
    label: 'Invoke Agent',
    category: 'tasks',
    icon: 'Bot',
    description: 'Execute deployed agent with prompt',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'agentId',
        label: 'Agent',
        type: 'select',
        required: true,
        options: [],
        helpText: 'Select deployed agent',
      },
      {
        field: 'prompt',
        label: 'Prompt/Instructions',
        type: 'textarea',
        required: true,
        placeholder: 'Analyze this security incident and recommend actions',
        helpText: 'Prompt for agent (supports {{variables}})',
      },
      {
        field: 'timeout',
        label: 'Timeout (seconds)',
        type: 'number',
        required: false,
        default: 120,
        helpText: 'Agent execution timeout',
      },
      {
        field: 'maxRetries',
        label: 'Max Retries',
        type: 'number',
        required: false,
        default: 1,
        helpText: 'Number of retries on failure',
      },
    ],
    outputSchema: {
      agentId: 'string',
      response: 'string|object',
      success: 'boolean',
      duration: 'number',
      tokenUsage: 'object',
    },
    tags: ['agent', 'ai', 'automation'],
  },

  skill_task: {
    type: 'skill_task',
    label: 'Execute Skill',
    category: 'tasks',
    icon: 'Zap',
    description: 'Execute specific skill from an agent',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'agentId',
        label: 'Agent',
        type: 'select',
        required: true,
        options: [],
        helpText: 'Select agent containing the skill',
      },
      {
        field: 'skillName',
        label: 'Skill Name',
        type: 'select',
        required: true,
        options: [],
        helpText: 'Select skill from agent',
      },
      {
        field: 'input',
        label: 'Skill Input',
        type: 'json',
        required: false,
        placeholder: '{"inputKey": "value"}',
        helpText: 'Input parameters for skill',
      },
      {
        field: 'timeout',
        label: 'Timeout (seconds)',
        type: 'number',
        required: false,
        default: 60,
        helpText: 'Skill execution timeout',
      },
    ],
    outputSchema: {
      skillName: 'string',
      result: 'object',
      success: 'boolean',
      duration: 'number',
    },
    tags: ['skill', 'agent', 'execution'],
  },

  // ────────────────────────────────────────────────────────────────────────
  // AI / VECTOR / DOCUMENT nodes — introduced in M1 for Conductor OSS AI-task parity.
  // Backend canonical types: AI_EMBEDDINGS_GENERATE, AI_VECTOR_STORE,
  // AI_VECTOR_SEARCH, AI_DOCUMENT_PARSE, AI_TEXT_CHUNK, FILE_LIST, POLLING_HTTP.
  // ────────────────────────────────────────────────────────────────────────

  embeddings_task: {
    type: 'embeddings_task',
    label: 'Generate Embeddings',
    category: 'ai_rag',
    icon: 'Binary',
    description: 'Create a vector embedding from text using an LLM provider',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'llmProvider',
        label: 'Provider',
        type: 'select',
        required: true,
        options: ['openai', 'azure_openai', 'anthropic', 'bedrock', 'gemini', 'voyageai'],
        default: 'openai',
        helpText: 'Embeddings provider',
      },
      {
        field: 'model',
        label: 'Embedding Model',
        type: 'select',
        required: true,
        options: ['text-embedding-3-small', 'text-embedding-3-large', 'text-embedding-ada-002',
          'amazon.titan-embed-text-v2:0', 'text-embedding-004',
          'voyage-3', 'voyage-3-large', 'voyage-3-lite', 'voyage-code-3', 'voyage-law-2', 'voyage-finance-2', 'voyage-multimodal-3'],
        default: 'text-embedding-3-small',
      },
      {
        field: 'text',
        label: 'Text',
        type: 'textarea',
        required: true,
        placeholder: '{{upstream.output.text}}',
        helpText: 'Text to embed — supports {{variable}} placeholders',
      },
    ],
    outputSchema: {
      embedding: 'number[]',
      dimensions: 'integer',
      model: 'string',
      tokensUsed: 'integer',
    },
    tags: ['ai', 'embeddings', 'vector'],
  },

  vector_store_task: {
    type: 'vector_store_task',
    label: 'Vector Store (Upsert)',
    category: 'ai_rag',
    icon: 'Database',
    description: 'Upsert embeddings or documents into a vector database',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'vectorStore',
        label: 'Vector Store',
        type: 'select',
        required: true,
        options: ['pinecone', 'weaviate', 'pgvector', 'mongo_atlas'],
        default: 'pinecone',
      },
      {
        field: 'index',
        label: 'Index / Collection',
        type: 'text',
        required: true,
        placeholder: 'my-index',
      },
      {
        field: 'namespace',
        label: 'Namespace',
        type: 'text',
        required: false,
        placeholder: 'default',
      },
      {
        field: 'documentId',
        label: 'Document ID',
        type: 'text',
        required: false,
        placeholder: '{{upstream.output.id}}',
      },
      {
        field: 'embeddings',
        label: 'Embeddings (array)',
        type: 'textarea',
        required: false,
        helpText: 'Pre-computed embeddings ({{upstream.output.embedding}}) — OR provide text below to auto-embed',
      },
      {
        field: 'text',
        label: 'Text (auto-embed)',
        type: 'textarea',
        required: false,
        helpText: 'If provided, service will embed then upsert using embeddingModel',
      },
      {
        field: 'embeddingModel',
        label: 'Embedding Model (if text provided)',
        type: 'text',
        required: false,
      },
      {
        field: 'metadata',
        label: 'Metadata (JSON)',
        type: 'json',
        required: false,
        placeholder: '{"source": "doc-42"}',
      },
    ],
    outputSchema: {
      upsertedCount: 'integer',
      index: 'string',
    },
    tags: ['ai', 'vector', 'rag', 'store'],
  },

  vector_search_task: {
    type: 'vector_search_task',
    label: 'Vector Search',
    category: 'ai_rag',
    icon: 'SearchCheck',
    description: 'Semantic search against a vector database',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'vectorStore',
        label: 'Vector Store',
        type: 'select',
        required: true,
        options: ['pinecone', 'weaviate', 'pgvector', 'mongo_atlas'],
        default: 'pinecone',
      },
      {
        field: 'index',
        label: 'Index / Collection',
        type: 'text',
        required: true,
      },
      {
        field: 'namespace',
        label: 'Namespace',
        type: 'text',
        required: false,
      },
      {
        field: 'query',
        label: 'Query Text',
        type: 'textarea',
        required: false,
        helpText: 'Free-text query (will be embedded) — OR provide queryVector below',
      },
      {
        field: 'queryVector',
        label: 'Query Vector',
        type: 'textarea',
        required: false,
        placeholder: '{{embeddings_task.output.embedding}}',
      },
      {
        field: 'embeddingModel',
        label: 'Embedding Model (for text query)',
        type: 'text',
        required: false,
      },
      {
        field: 'topK',
        label: 'Top K',
        type: 'number',
        required: true,
        default: 5,
      },
      {
        field: 'filter',
        label: 'Metadata Filter (JSON)',
        type: 'json',
        required: false,
      },
    ],
    outputSchema: {
      matches: 'array',
      topK: 'integer',
    },
    tags: ['ai', 'vector', 'rag', 'search'],
  },

  parse_document_task: {
    type: 'parse_document_task',
    label: 'Parse Document',
    category: 'ai_rag',
    icon: 'FileText',
    description: 'Parse a PDF, DOCX, or HTML document into structured text',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'sourceUrl',
        label: 'Source URL',
        type: 'text',
        required: true,
        placeholder: 'https://... or s3://bucket/key',
      },
      {
        field: 'mediaType',
        label: 'Media Type',
        type: 'select',
        required: false,
        options: [
          { label: 'Auto-detect', value: 'auto' },
          { label: 'PDF (.pdf)', value: 'application/pdf' },
          { label: 'Word Document (.docx)', value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
          { label: 'HTML (.html)', value: 'text/html' },
          { label: 'Plain Text (.txt)', value: 'text/plain' },
          { label: 'Markdown (.md)', value: 'text/markdown' },
        ],
        default: 'auto',
      },
    ],
    outputSchema: {
      text: 'string',
      pageCount: 'integer',
      metadata: 'object',
    },
    tags: ['ai', 'document', 'parse', 'rag'],
  },

  chunk_text_task: {
    type: 'chunk_text_task',
    label: 'Chunk Text',
    category: 'ai_rag',
    icon: 'Scissors',
    description: 'Split long text into overlapping chunks for embedding or summarization',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'text',
        label: 'Text',
        type: 'textarea',
        required: true,
        placeholder: '{{parse_document_task.output.text}}',
      },
      {
        field: 'documentId',
        label: 'Document ID',
        type: 'text',
        required: false,
      },
      {
        field: 'chunkSize',
        label: 'Chunk Size (chars)',
        type: 'number',
        required: true,
        default: 1000,
      },
      {
        field: 'chunkOverlap',
        label: 'Overlap (chars)',
        type: 'number',
        required: false,
        default: 100,
      },
      {
        field: 'separator',
        label: 'Separator',
        type: 'select',
        required: false,
        options: ['paragraph', 'sentence', 'newline', 'custom'],
        default: 'paragraph',
      },
    ],
    outputSchema: {
      chunks: 'array',
      chunkCount: 'integer',
    },
    tags: ['ai', 'text', 'chunk', 'rag'],
  },

  file_list_task: {
    type: 'file_list_task',
    label: 'List Files',
    category: 'tasks',
    icon: 'Folder',
    description: 'List files from a storage connector (S3, GCS, SharePoint, etc.)',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connector',
        label: 'Storage Connector',
        type: 'select',
        required: true,
        options: ['aws_s3', 'gcs', 'sharepoint', 'box', 'dropbox'],
        default: 'aws_s3',
      },
      {
        field: 'bucket',
        label: 'Bucket / Container',
        type: 'text',
        required: true,
      },
      {
        field: 'prefix',
        label: 'Prefix / Path',
        type: 'text',
        required: false,
      },
    ],
    outputSchema: {
      objects: 'array',
      count: 'integer',
      isTruncated: 'boolean',
    },
    tags: ['file', 'storage', 'list'],
  },

  // ==========================================================================
  // RC-104 — Storage + Secrets quartet (BE handlers added in this RC)
  // ==========================================================================
  //
  // file_read_task / file_write_task route through executeConnectorTask
  // (action injected by the workflow case branch) against any storage
  // connector (S3, GCS, SharePoint, Box, Dropbox, local FS). file_list_task
  // (above) was already a NODE_TYPES entry but its BE case branch was
  // missing — fixed in this same RC.
  //
  // secret_read_task / secret_write_task route through executeConnectorTask
  // against any vault-backed connector (HashiCorp Vault, GCP Secret Manager,
  // AWS Secrets Manager, Azure Key Vault). Connector implementation handles
  // the vault:// reference resolution and the audit trail that secret
  // access requires.
  //
  // BE enum entries (CanonicalNodeType.FILE_READ / FILE_WRITE / SECRET_READ
  // / SECRET_WRITE) already existed in the IR — same situation as form_task.

  file_read_task: {
    type: 'file_read_task',
    label: 'Read File',
    category: 'tasks',
    icon: 'FileDown',
    description: 'Read a single file from a storage connector (S3, GCS, SharePoint, etc.). Returns content (text or base64) plus metadata.',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connectorId',
        label: 'Storage Connector',
        type: 'text',
        required: true,
        placeholder: 'UUID of a configured storage connector',
        helpText: 'Configured at Admin > Connectors. Connector type can be S3, GCS, Azure Blob, SharePoint, Box, Dropbox, or local filesystem.',
      },
      {
        field: 'path',
        label: 'File Path',
        type: 'text',
        required: true,
        placeholder: 'bucket/folder/document.pdf or ${trigger-1_output.filePath}',
        helpText: 'Path to the file within the storage connector. Supports ${...} variable references.',
      },
      {
        field: 'encoding',
        label: 'Output Encoding',
        type: 'select',
        required: false,
        default: 'auto',
        options: [
          { value: 'auto', label: 'Auto (text for text/*, base64 for binary)' },
          { value: 'utf8', label: 'UTF-8 text' },
          { value: 'base64', label: 'Base64 (always)' },
        ],
        helpText: 'How the file content is returned. Auto inspects the connector\'s reported content-type.',
      },
      {
        field: 'failIfMissing',
        label: 'Fail if file missing',
        type: 'boolean',
        required: false,
        default: true,
        helpText: 'When false, missing files return content=null with status=completed instead of failing the node.',
      },
    ],
    outputSchema: {
      path: 'string',
      content: 'string',
      contentBase64: 'string',
      contentType: 'string',
      size: 'number',
      lastModified: 'ISO8601',
      exists: 'boolean',
    },
    tags: ['file', 'storage', 'read', 'download'],
  },

  file_write_task: {
    type: 'file_write_task',
    label: 'Write File',
    category: 'tasks',
    icon: 'FileUp',
    description: 'Write a single file to a storage connector. Supports inline text content or base64-encoded binary.',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connectorId',
        label: 'Storage Connector',
        type: 'text',
        required: true,
        placeholder: 'UUID of a configured storage connector',
      },
      {
        field: 'path',
        label: 'Destination Path',
        type: 'text',
        required: true,
        placeholder: 'bucket/reports/${trigger-1_output.runId}.pdf',
        helpText: 'Destination path. Supports ${...} variables.',
      },
      {
        field: 'content',
        label: 'Content (text)',
        type: 'textarea',
        required: false,
        placeholder: '${upstream_step.reportBody}',
        helpText: 'Text content to write. Either content OR contentBase64 must be provided.',
      },
      {
        field: 'contentBase64',
        label: 'Content (base64)',
        type: 'textarea',
        required: false,
        placeholder: '${upstream_step.pdfBytesBase64}',
        helpText: 'Base64-encoded binary content. Use for PDFs, images, etc.',
      },
      {
        field: 'contentType',
        label: 'Content-Type',
        type: 'text',
        required: false,
        placeholder: 'application/pdf',
        helpText: 'MIME type. Auto-detected from path extension when omitted.',
      },
      {
        field: 'overwrite',
        label: 'Overwrite if exists',
        type: 'boolean',
        required: false,
        default: true,
        helpText: 'When false, the node fails if the destination already exists.',
      },
    ],
    outputSchema: {
      path: 'string',
      url: 'string',
      size: 'number',
      etag: 'string',
      versionId: 'string',
    },
    tags: ['file', 'storage', 'write', 'upload'],
  },

  secret_read_task: {
    type: 'secret_read_task',
    label: 'Read Secret',
    category: 'tasks',
    icon: 'KeyRound',
    description: 'Resolve a vault:// reference to its plaintext value at runtime (audited). Reads from HashiCorp Vault, GCP Secret Manager, AWS Secrets Manager, or Azure Key Vault via the configured vault connector.',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connectorId',
        label: 'Vault Connector',
        type: 'text',
        required: true,
        placeholder: 'UUID of a configured vault connector',
        helpText: 'Configured at Admin > Connectors. Connector type can be hashicorp_vault, gcp_secret_manager, aws_secrets_manager, or azure_key_vault.',
      },
      {
        field: 'reference',
        label: 'Vault Reference',
        type: 'text',
        required: true,
        placeholder: 'vault://default/tenant-.../connectors/.../api-key',
        helpText: 'Full vault:// wire reference, OR a connector-relative path. Supports ${...} variables.',
      },
      {
        field: 'failIfMissing',
        label: 'Fail if reference missing',
        type: 'boolean',
        required: false,
        default: true,
      },
    ],
    outputSchema: {
      reference: 'string',
      plaintext: 'string',          // sensitive — do not log downstream
      resolved: 'boolean',
      backend: 'string',
      auditId: 'string',
    },
    tags: ['secret', 'vault', 'read', 'credentials', 'audited'],
  },

  secret_write_task: {
    type: 'secret_write_task',
    label: 'Write Secret',
    category: 'tasks',
    icon: 'KeyRound',
    description: 'Store a new secret value or rotate an existing one. Returns the vault:// reference for downstream nodes to use. Every write is audited.',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connectorId',
        label: 'Vault Connector',
        type: 'text',
        required: true,
        placeholder: 'UUID of a configured vault connector',
      },
      {
        field: 'mode',
        label: 'Mode',
        type: 'select',
        required: true,
        default: 'store',
        options: [
          { value: 'store', label: 'Store new (fail if exists)' },
          { value: 'rotate', label: 'Rotate existing (must exist)' },
          { value: 'upsert', label: 'Upsert (store or rotate)' },
        ],
      },
      {
        field: 'resourceType',
        label: 'Resource Type',
        type: 'text',
        required: false,
        placeholder: 'connectors, agents, tenant-config',
        helpText: 'Logical owner of the secret. Used in the canonical vault path: tenant-<tenantId>/<resourceType>/<resourceId>/<fieldName>.',
      },
      {
        field: 'resourceId',
        label: 'Resource ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.connectorId}',
      },
      {
        field: 'fieldName',
        label: 'Field Name',
        type: 'text',
        required: true,
        placeholder: 'api-key, oauth-refresh-token',
        helpText: 'The leaf field name. Becomes the final segment of the vault path.',
      },
      {
        field: 'plaintext',
        label: 'Plaintext Value',
        type: 'textarea',
        required: true,
        placeholder: '${upstream_step.newApiKey}',
        helpText: 'The secret value to store. NEVER hard-code real secrets here — reference an upstream node\'s output. Stored encrypted at rest by the vault backend.',
      },
      {
        field: 'reference',
        label: 'Vault Reference (for rotate)',
        type: 'text',
        required: false,
        placeholder: 'vault://...',
        helpText: 'Required when mode=rotate. The existing reference whose underlying value is being replaced.',
      },
    ],
    outputSchema: {
      reference: 'string',
      backend: 'string',
      path: 'string',
      rotated: 'boolean',
      auditId: 'string',
    },
    tags: ['secret', 'vault', 'write', 'rotate', 'credentials', 'audited'],
  },

  document_upload_task: {
    type: 'document_upload_task',
    label: 'Upload Document',
    category: 'ai_rag',
    icon: 'Upload',
    description: 'Accept an uploaded document (or pre-uploaded URL) as the entry point for a RAG ingest pipeline',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'source',
        label: 'Source',
        type: 'select',
        required: true,
        options: ['upload', 'url'],
        default: 'upload',
        helpText: 'upload = accept file at run time; url = fetch from a pre-uploaded storage URL',
      },
      {
        field: 'fileUrl',
        label: 'File URL',
        type: 'text',
        required: false,
        placeholder: 's3://bucket/key.pdf or https://...',
        helpText: 'Required when source=url. At upload time this is stamped with the uploaded object URL.',
      },
      {
        field: 'fileName',
        label: 'File Name',
        type: 'text',
        required: false,
        placeholder: 'policy-2026.pdf',
      },
      {
        field: 'mediaType',
        label: 'Media Type',
        type: 'select',
        required: false,
        options: [
          { label: 'Auto-detect', value: 'auto' },
          { label: 'PDF (.pdf)', value: 'application/pdf' },
          { label: 'Word Document (.docx)', value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
          { label: 'HTML (.html)', value: 'text/html' },
          { label: 'Plain Text (.txt)', value: 'text/plain' },
          { label: 'Markdown (.md)', value: 'text/markdown' },
        ],
        default: 'auto',
      },
      {
        field: 'allowedMediaTypes',
        label: 'Allowed Media Types',
        type: 'multiselect',
        required: false,
        options: [
          { label: 'PDF (.pdf)', value: 'application/pdf' },
          { label: 'Word Document (.docx)', value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
          { label: 'HTML (.html)', value: 'text/html' },
          { label: 'Plain Text (.txt)', value: 'text/plain' },
          { label: 'Markdown (.md)', value: 'text/markdown' },
        ],
        default: ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        helpText: 'Uploads outside this set are rejected before the workflow starts.',
      },
      {
        field: 'maxSizeMb',
        label: 'Max Size (MB)',
        type: 'number',
        required: false,
        default: 50,
      },
      {
        field: 'storageConnector',
        label: 'Storage Connector',
        type: 'select',
        required: false,
        options: ['platform_default', 'aws_s3', 'gcs', 'azure_blob'],
        default: 'platform_default',
        helpText: 'Where uploaded files are persisted. platform_default uses the tenant\u2019s configured document store.',
      },
      {
        field: 'documentId',
        label: 'Document ID',
        type: 'text',
        required: false,
        placeholder: '{{workflow.id}}-{{fileName}}',
        helpText: 'Leave blank to auto-generate from workflow ID + filename.',
      },
      {
        field: 'tenantId',
        label: 'Tenant ID',
        type: 'text',
        required: false,
        placeholder: '{{workflow.input.tenantId}}',
      },
      {
        field: 'metadata',
        label: 'Metadata (JSON)',
        type: 'textarea',
        required: false,
        placeholder: '{"classification":"internal","author":"legal"}',
      },
    ],
    outputSchema: {
      fileUrl: 'string',
      fileName: 'string',
      mediaType: 'string',
      sizeBytes: 'integer',
      documentId: 'string',
      uploadedAt: 'string',
    },
    tags: ['ai', 'upload', 'document', 'rag', 'ingest'],
  },

  http_poll_task: {
    type: 'http_poll_task',
    label: 'HTTP Poll',
    category: 'tasks',
    icon: 'Repeat',
    description: 'Call an HTTP endpoint repeatedly until a condition holds or max attempts reached',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'default', label: 'Complete' },
        { name: 'timeout', label: 'Timeout' },
      ],
    },
    configSchema: [
      {
        field: 'method',
        label: 'Method',
        type: 'select',
        required: true,
        options: ['GET', 'POST', 'PUT', 'DELETE'],
        default: 'GET',
      },
      {
        field: 'url',
        label: 'URL',
        type: 'text',
        required: true,
      },
      {
        field: 'terminationCondition',
        label: 'Termination Condition (expression)',
        type: 'textarea',
        required: true,
        placeholder: '$.response.status === "COMPLETED"',
        helpText: 'JSONPath/JS expression that must evaluate true to stop polling',
      },
      {
        field: 'pollingIntervalMs',
        label: 'Poll Interval (ms)',
        type: 'number',
        required: true,
        default: 5000,
      },
      {
        field: 'pollingStrategy',
        label: 'Strategy',
        type: 'select',
        required: false,
        options: ['FIXED', 'LINEAR', 'EXPONENTIAL_BACKOFF'],
        default: 'FIXED',
      },
      {
        field: 'maxAttempts',
        label: 'Max Attempts',
        type: 'number',
        required: true,
        default: 60,
      },
    ],
    outputSchema: {
      response: 'object',
      attempts: 'integer',
      status: 'string',
    },
    tags: ['http', 'poll', 'wait'],
  },

  // ==========================================================================
  // RC-102a — Class A node types (BE handler already wired)
  // ==========================================================================
  //
  // The picker was advertising these for months as if they existed but
  // there was no NODE_TYPES entry; operators saw them in the dropdown
  // and selected them but the canvas had no schema to render. The BE
  // side was always live — DAGNodeActivitiesImpl handles each via
  // executeEmailTask (email_*) or executeConnectorTask after stripping
  // the `_action` suffix (slack/teams/pagerduty/jira/servicenow_action).
  //
  // These configSchemas thread the integration-specific fields. Each
  // also accepts an optional `connectorId` — when blank, the BE
  // auto-discovers the appropriate connector for the tenant (e.g.,
  // executeEmailTask falls back to the tenant's default SMTP connector).
  //
  // Class B (form_task, database_task, file_read/write_task,
  // secret_read/write_task, rpc_task, event_publish, webhook_callback_task)
  // need new Temporal activities first; see follow-up RCs after RC-102a.

  email_task: {
    type: 'email_task',
    label: 'Send Email',
    category: 'tasks',
    icon: 'Mail',
    description: 'Send an email via the tenant\'s SMTP connector. Auto-discovers the configured Email connector when connectorId is blank.',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connectorId',
        label: 'Email Connector (optional)',
        type: 'text',
        required: false,
        placeholder: 'leave blank for tenant default',
        helpText: 'UUID of a configured Email/SMTP connector. Blank = BE auto-discovers the tenant\'s default SMTP connector.',
      },
      {
        field: 'to',
        label: 'To',
        type: 'text',
        required: true,
        placeholder: 'alerts@company.com, ${trigger-1_output.userEmail}',
        helpText: 'Comma-separated recipient addresses. Supports ${...} variable references.',
      },
      {
        field: 'cc',
        label: 'CC',
        type: 'text',
        required: false,
        placeholder: 'optional CC recipients',
      },
      {
        field: 'bcc',
        label: 'BCC',
        type: 'text',
        required: false,
        placeholder: 'optional BCC recipients',
      },
      {
        field: 'fromAddress',
        label: 'From (override)',
        type: 'text',
        required: false,
        placeholder: 'noreply@yourdomain.com',
        helpText: 'Optional sender override; defaults to the connector\'s configured From address.',
      },
      {
        field: 'subject',
        label: 'Subject',
        type: 'text',
        required: true,
        placeholder: '[Prove7] Approval needed for {{workflowName}}',
        helpText: 'Email subject. Supports ${...} variables and {{template}} placeholders.',
      },
      {
        field: 'templateId',
        label: 'Email Template (optional)',
        type: 'template_select',
        required: false,
        templateType: 'email',
        helpText: 'Pick a template from the Template Center, or leave blank and author body inline below.',
      },
      {
        field: 'body',
        label: 'Body (HTML)',
        type: 'textarea',
        required: false,
        placeholder: '<p>Hello {{name}},</p>...',
        helpText: 'HTML email body. Used when no templateId is selected.',
      },
      {
        field: 'attachments',
        label: 'Attachments',
        type: 'json',
        required: false,
        placeholder: '[{"filename": "report.pdf", "url": "${prev_step.reportUrl}"}]',
        helpText: 'Array of {filename, url} or {filename, contentBase64}. Optional.',
      },
    ],
    outputSchema: {
      messageId: 'string',
      sent: 'boolean',
      acceptedRecipients: 'array',
      rejectedRecipients: 'array',
      timestamp: 'ISO8601',
    },
    tags: ['email', 'smtp', 'notification', 'communication'],
  },

  email_send: {
    type: 'email_send',
    label: 'Send Email (legacy alias)',
    category: 'tasks',
    icon: 'Mail',
    description: 'Alias of email_task. BE routes both to executeEmailTask; canonical entry carries the schema.',
    color: '#0d9488',
    aliasOf: 'email_task',
    paletteHidden: true,   // RC-102a — alias only; canonical entry surfaces in palette
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [],
    outputSchema: {},
    tags: ['email', 'alias'],
  },

  email_smtp: {
    type: 'email_smtp',
    label: 'Send Email via SMTP',
    category: 'tasks',
    icon: 'Mail',
    description: 'Force SMTP-specific send path (bypasses connector auto-discovery). Use when the tenant has multiple email connectors and you must route via a specific SMTP host.',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    // Same schema as email_task but with connectorId REQUIRED (no auto-discovery).
    configSchema: [
      {
        field: 'connectorId',
        label: 'SMTP Connector',
        type: 'text',
        required: true,
        placeholder: 'UUID of a configured SMTP connector',
        helpText: 'Required — explicit SMTP connector. To use the tenant default, prefer email_task instead.',
      },
      { field: 'to', label: 'To', type: 'text', required: true, placeholder: 'alerts@company.com' },
      { field: 'cc', label: 'CC', type: 'text', required: false },
      { field: 'subject', label: 'Subject', type: 'text', required: true },
      { field: 'body', label: 'Body (HTML)', type: 'textarea', required: true, placeholder: '<p>...</p>' },
    ],
    outputSchema: {
      messageId: 'string',
      sent: 'boolean',
      timestamp: 'ISO8601',
    },
    tags: ['email', 'smtp', 'explicit'],
  },

  slack_action: {
    type: 'slack_action',
    label: 'Slack Action',
    category: 'tasks',
    icon: 'MessageSquare',
    description: 'Post a message, file, or reaction to Slack via the Slack connector. BE strips the _action suffix and routes through executeConnectorTask.',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connectorId',
        label: 'Slack Connector',
        type: 'text',
        required: true,
        placeholder: 'UUID of the tenant\'s Slack connector',
        helpText: 'Slack workspace credentials are held by the connector — pick which one to use.',
      },
      {
        field: 'action',
        label: 'Action',
        type: 'select',
        required: true,
        options: ['post_message', 'post_thread_reply', 'upload_file', 'add_reaction', 'update_message'],
        default: 'post_message',
        helpText: 'Which Slack API operation to perform.',
      },
      {
        field: 'channel',
        label: 'Channel',
        type: 'text',
        required: true,
        placeholder: '#incidents or C0123456789',
        helpText: 'Channel name (#prefix) or channel ID. Supports ${...} variables.',
      },
      {
        field: 'message',
        label: 'Message Text',
        type: 'textarea',
        required: false,
        placeholder: ':rotating_light: Alert: ${trigger-1_output.summary}',
        helpText: 'Slack Markdown / mrkdwn text. Required for post_message and post_thread_reply.',
      },
      {
        field: 'threadTs',
        label: 'Thread Timestamp',
        type: 'text',
        required: false,
        placeholder: '${prev_slack_post.messageTs}',
        helpText: 'Required for post_thread_reply and update_message. Get from a prior Slack action\'s output.',
      },
      {
        field: 'blocks',
        label: 'Block Kit Blocks (JSON)',
        type: 'json',
        required: false,
        placeholder: '[{"type": "section", "text": {"type": "mrkdwn", "text": "..."}}]',
        helpText: 'Rich Slack Block Kit payload. Overrides message text when provided.',
      },
    ],
    outputSchema: {
      messageTs: 'string',
      channel: 'string',
      permalink: 'string',
      ok: 'boolean',
    },
    tags: ['slack', 'notification', 'chat', 'communication'],
  },

  teams_action: {
    type: 'teams_action',
    label: 'Microsoft Teams Action',
    category: 'tasks',
    icon: 'MessageSquare',
    description: 'Post to a Teams channel or send a chat message via the Teams connector. BE routes via executeConnectorTask("teams", ...).',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connectorId',
        label: 'Teams Connector',
        type: 'text',
        required: true,
        placeholder: 'UUID of the tenant\'s Teams connector',
      },
      {
        field: 'action',
        label: 'Action',
        type: 'select',
        required: true,
        options: ['post_to_channel', 'post_to_chat', 'adaptive_card'],
        default: 'post_to_channel',
      },
      {
        field: 'teamId',
        label: 'Team ID',
        type: 'text',
        required: false,
        placeholder: 'GUID of the Team (post_to_channel only)',
      },
      {
        field: 'channelId',
        label: 'Channel ID',
        type: 'text',
        required: false,
        placeholder: 'GUID of the Channel (post_to_channel only)',
      },
      {
        field: 'chatId',
        label: 'Chat ID',
        type: 'text',
        required: false,
        placeholder: 'For post_to_chat — direct/group chat thread id',
      },
      {
        field: 'message',
        label: 'Message (HTML)',
        type: 'textarea',
        required: false,
        placeholder: '<b>Alert</b>: ${trigger-1_output.summary}',
        helpText: 'HTML message body. Required unless using adaptive_card.',
      },
      {
        field: 'adaptiveCard',
        label: 'Adaptive Card (JSON)',
        type: 'json',
        required: false,
        placeholder: '{"type": "AdaptiveCard", "version": "1.5", "body": [...]}',
        helpText: 'Adaptive Card payload. Required when action=adaptive_card.',
      },
    ],
    outputSchema: {
      messageId: 'string',
      ok: 'boolean',
      timestamp: 'ISO8601',
    },
    tags: ['teams', 'microsoft', 'notification', 'chat'],
  },

  pagerduty_action: {
    type: 'pagerduty_action',
    label: 'PagerDuty Action',
    category: 'tasks',
    icon: 'AlertTriangle',
    description: 'Trigger, acknowledge, or resolve a PagerDuty incident via the PagerDuty connector.',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connectorId',
        label: 'PagerDuty Connector',
        type: 'text',
        required: true,
        placeholder: 'UUID of the tenant\'s PagerDuty connector',
      },
      {
        field: 'action',
        label: 'Action',
        type: 'select',
        required: true,
        options: ['trigger', 'acknowledge', 'resolve'],
        default: 'trigger',
        helpText: 'PagerDuty Events API v2 action.',
      },
      {
        field: 'routingKey',
        label: 'Routing Key (override)',
        type: 'text',
        required: false,
        placeholder: 'override the connector\'s default integration key',
        helpText: 'Per-service routing key. Defaults to the connector\'s integration key when blank.',
      },
      {
        field: 'dedupKey',
        label: 'Dedup Key',
        type: 'text',
        required: false,
        placeholder: 'incident-${trigger-1_output.incidentId}',
        helpText: 'Idempotency key. Required for acknowledge/resolve; auto-generated for trigger if blank.',
      },
      {
        field: 'severity',
        label: 'Severity',
        type: 'select',
        required: false,
        options: ['critical', 'error', 'warning', 'info'],
        default: 'error',
        helpText: 'For action=trigger only.',
      },
      {
        field: 'summary',
        label: 'Summary',
        type: 'text',
        required: false,
        placeholder: 'Critical CVE detected: ${trigger-1_output.cveId}',
        helpText: 'Required for action=trigger. Shown as the incident title.',
      },
      {
        field: 'source',
        label: 'Source',
        type: 'text',
        required: false,
        placeholder: 'prove7-vulnerability-scanner',
        helpText: 'Unique source identifier for trigger.',
      },
      {
        field: 'customDetails',
        label: 'Custom Details (JSON)',
        type: 'json',
        required: false,
        placeholder: '{"cveId": "${...}", "cvssScore": "${...}"}',
        helpText: 'Arbitrary key/value pairs attached to the incident.',
      },
    ],
    outputSchema: {
      dedupKey: 'string',
      status: 'string',
      message: 'string',
    },
    tags: ['pagerduty', 'incident', 'alerting', 'oncall'],
  },

  jira_action: {
    type: 'jira_action',
    label: 'Jira Action',
    category: 'tasks',
    icon: 'CheckSquare',
    description: 'Create, update, transition, or comment on Jira issues via the Jira connector.',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connectorId',
        label: 'Jira Connector',
        type: 'text',
        required: true,
        placeholder: 'UUID of the tenant\'s Jira connector',
      },
      {
        field: 'action',
        label: 'Action',
        type: 'select',
        required: true,
        options: ['create_issue', 'update_issue', 'transition', 'add_comment', 'link_issues'],
        default: 'create_issue',
      },
      {
        field: 'projectKey',
        label: 'Project Key',
        type: 'text',
        required: false,
        placeholder: 'SEC, INC, OPS',
        helpText: 'Jira project key. Required for create_issue.',
      },
      {
        field: 'issueKey',
        label: 'Issue Key',
        type: 'text',
        required: false,
        placeholder: 'SEC-1234',
        helpText: 'Required for update_issue, transition, add_comment, link_issues.',
      },
      {
        field: 'issueType',
        label: 'Issue Type',
        type: 'select',
        required: false,
        options: ['Bug', 'Task', 'Incident', 'Story', 'Epic'],
        default: 'Task',
      },
      {
        field: 'summary',
        label: 'Summary',
        type: 'text',
        required: false,
        placeholder: '[Prove7] ${trigger-1_output.alertTitle}',
      },
      {
        field: 'description',
        label: 'Description',
        type: 'textarea',
        required: false,
        placeholder: 'Jira ADF or markdown body',
      },
      {
        field: 'labels',
        label: 'Labels (comma-separated)',
        type: 'text',
        required: false,
        placeholder: 'prove7, automated, ${severity}',
      },
      {
        field: 'transitionName',
        label: 'Transition Name',
        type: 'text',
        required: false,
        placeholder: 'In Progress, Done',
        helpText: 'For action=transition. Must match an available transition for the issue\'s current status.',
      },
      {
        field: 'fields',
        label: 'Custom Fields (JSON)',
        type: 'json',
        required: false,
        placeholder: '{"customfield_10001": "...", "assignee": {"accountId": "..."}}',
      },
    ],
    outputSchema: {
      issueKey: 'string',
      issueId: 'string',
      url: 'string',
      status: 'string',
    },
    tags: ['jira', 'ticket', 'itsm', 'atlassian'],
  },

  servicenow_action: {
    type: 'servicenow_action',
    label: 'ServiceNow Action',
    category: 'tasks',
    icon: 'ClipboardList',
    description: 'Create or update a ServiceNow incident, change request, or other table record via the ServiceNow connector.',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connectorId',
        label: 'ServiceNow Connector',
        type: 'text',
        required: true,
        placeholder: 'UUID of the tenant\'s ServiceNow connector',
      },
      {
        field: 'action',
        label: 'Action',
        type: 'select',
        required: true,
        options: ['create_record', 'update_record', 'query_records'],
        default: 'create_record',
      },
      {
        field: 'table',
        label: 'Table',
        type: 'select',
        required: true,
        options: ['incident', 'change_request', 'problem', 'sc_request', 'cmdb_ci', 'custom'],
        default: 'incident',
        helpText: 'ServiceNow table name. Pick "custom" to type a different table.',
      },
      {
        field: 'customTable',
        label: 'Custom Table Name',
        type: 'text',
        required: false,
        placeholder: 'u_security_event',
        helpText: 'Required when table=custom.',
      },
      {
        field: 'sysId',
        label: 'sys_id',
        type: 'text',
        required: false,
        placeholder: 'sys_id of the record',
        helpText: 'Required for update_record.',
      },
      {
        field: 'query',
        label: 'Query (encoded)',
        type: 'text',
        required: false,
        placeholder: 'active=true^priority=1',
        helpText: 'ServiceNow encoded query string. For action=query_records.',
      },
      {
        field: 'fields',
        label: 'Fields (JSON)',
        type: 'json',
        required: false,
        placeholder: '{"short_description": "...", "priority": "1", "assigned_to": "..."}',
        helpText: 'Field key/value map for create_record and update_record.',
      },
    ],
    outputSchema: {
      sysId: 'string',
      number: 'string',
      url: 'string',
      records: 'array',
    },
    tags: ['servicenow', 'itsm', 'incident', 'cmdb'],
  },

  message_consume_task: {
    type: 'message_consume_task',
    label: 'Consume from Queue',
    category: 'tasks',
    icon: 'Inbox',
    description: 'Pull message(s) from a queue or topic (Kafka, SQS, RabbitMQ, etc.) via the connector. BE routes through executeConnectorTask.',
    color: '#0d9488',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connectorId',
        label: 'Queue Connector',
        type: 'text',
        required: true,
        placeholder: 'UUID of a Kafka / SQS / RabbitMQ / Pub-Sub connector',
        helpText: 'Connector that knows how to read from the underlying broker.',
      },
      {
        field: 'queueOrTopic',
        label: 'Queue / Topic Name',
        type: 'text',
        required: true,
        placeholder: 'security-events, my-kafka-topic',
      },
      {
        field: 'consumerGroup',
        label: 'Consumer Group',
        type: 'text',
        required: false,
        placeholder: 'prove7-workflow-consumer',
        helpText: 'For Kafka / Pub-Sub style brokers. Ignored for SQS / RabbitMQ.',
      },
      {
        field: 'batchSize',
        label: 'Batch Size',
        type: 'number',
        required: false,
        default: 1,
        helpText: 'Max messages to pull per execution. 1 = one-at-a-time.',
      },
      {
        field: 'waitSeconds',
        label: 'Long-Poll Wait (seconds)',
        type: 'number',
        required: false,
        default: 0,
        helpText: 'Long-poll wait time when queue is empty. 0 = return immediately.',
      },
      {
        field: 'autoAck',
        label: 'Auto-Acknowledge',
        type: 'boolean',
        required: false,
        default: true,
        helpText: 'Auto-commit/ack on successful pull. Disable to defer ack until downstream success.',
      },
    ],
    outputSchema: {
      messages: 'array',
      count: 'integer',
      hasMore: 'boolean',
    },
    tags: ['queue', 'messaging', 'kafka', 'sqs', 'rabbitmq', 'pubsub'],
  },
};

// ============================================================================
// SECURITY OPERATIONS NODE TYPES (9)
// ============================================================================

const SECURITY_OPS_NODES = {
  threat_intel_lookup: {
    type: 'threat_intel_lookup',
    label: 'Threat Intelligence Lookup',
    category: 'security_ops',
    icon: 'Search',
    description: 'Enrich IOC (indicator of compromise) with threat intelligence',
    color: '#ef4444',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'iocType',
        label: 'IOC Type',
        type: 'select',
        required: true,
        /* RC-109b — Expanded from 5 → 9 options. Original set
         * (ip / domain / hash / url / email) was insufficient: 'cve'
         * is a legitimate IOC for vulnerability-triage flows (NVD,
         * Vulners, Recorded Future all index by CVE id), and 'asn',
         * 'cidr', 'mutex', 'registry_key' cover SOC/threat-hunt use
         * cases the previous list silently excluded. The pre-RC-109b
         * gap was the root cause of an operator confusion: the AI
         * Explainer recommended changing iocType to 'cve' for a CVE-
         * driven triage workflow but the dropdown didn't offer it. */
        options: ['ip', 'domain', 'hash', 'url', 'email', 'cve', 'asn', 'cidr', 'mutex', 'registry_key'],
        helpText: 'Type of indicator to look up. cve for vulnerability lookups (NVD/Vulners). asn/cidr for network-block intel. mutex/registry_key for host-artefact indicators.',
      },
      {
        field: 'iocValue',
        label: 'IOC Value',
        type: 'text',
        required: true,
        placeholder: '192.168.1.1 or {{input.ip}}',
        helpText: 'IOC value to lookup (supports templates)',
      },
      {
        field: 'sources',
        label: 'Intelligence Sources',
        type: 'multiselect',
        required: true,
        options: ['virustotal', 'alienvault', 'shodan', 'abuseipdb', 'urlhaus', 'malwarebazaar'],
        default: ['virustotal', 'alienvault'],
        helpText: 'Threat intelligence sources to query',
      },
      {
        field: 'timeout',
        label: 'Timeout (seconds)',
        type: 'number',
        required: false,
        default: 30,
        helpText: 'Lookup timeout',
      },
    ],
    outputSchema: {
      ioc: 'string',
      iocType: 'string',
      reputation: 'object',
      malicious: 'boolean',
      detections: 'array',
      sources: 'object',
      timestamp: 'ISO8601',
    },
    tags: ['threat-intel', 'enrichment', 'ioc'],
  },

  vulnerability_scan: {
    type: 'vulnerability_scan',
    label: 'Vulnerability Scan',
    category: 'security_ops',
    icon: 'AlertTriangle',
    description: 'Trigger vulnerability scan on target asset',
    color: '#ef4444',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'target',
        label: 'Target',
        type: 'text',
        required: true,
        placeholder: '192.168.1.100 or app.example.com',
        helpText: 'IP, hostname, or application URL to scan',
      },
      {
        field: 'scanType',
        label: 'Scan Type',
        type: 'select',
        required: true,
        options: ['quick', 'full', 'compliance'],
        default: 'quick',
        helpText: 'Type of vulnerability scan',
      },
      {
        field: 'scanProfile',
        label: 'Scan Profile',
        type: 'text',
        required: false,
        placeholder: 'pci-dss, hipaa, custom',
        helpText: 'Compliance or custom scan profile',
      },
      {
        field: 'waitForCompletion',
        label: 'Wait for Completion',
        type: 'boolean',
        required: false,
        default: false,
        helpText: 'Block until scan completes',
      },
    ],
    outputSchema: {
      scanId: 'string',
      target: 'string',
      status: 'string',
      vulnerabilities: 'array',
      criticalCount: 'integer',
      highCount: 'integer',
      startTime: 'ISO8601',
      endTime: 'ISO8601',
    },
    tags: ['security', 'scanning', 'assessment'],
  },

  asset_lookup: {
    type: 'asset_lookup',
    label: 'Asset Lookup',
    category: 'security_ops',
    icon: 'Database',
    description: 'Lookup asset from CMDB or inventory system',
    color: '#ef4444',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'assetType',
        label: 'Asset Type',
        type: 'select',
        required: true,
        options: ['host', 'user', 'application', 'network'],
        helpText: 'Type of asset to lookup',
      },
      {
        field: 'identifier',
        label: 'Identifier',
        type: 'text',
        required: true,
        placeholder: 'hostname, IP, email, or app ID',
        helpText: 'Asset identifier (supports {{variables}})',
      },
      {
        field: 'includeRelated',
        label: 'Include Related Assets',
        type: 'boolean',
        required: false,
        default: true,
        helpText: 'Include related/dependent assets',
      },
    ],
    outputSchema: {
      assetId: 'string',
      assetType: 'string',
      asset: 'object',
      relatedAssets: 'array',
      owner: 'object',
      lastUpdated: 'ISO8601',
    },
    tags: ['inventory', 'cmdb', 'asset'],
  },

  incident_create: {
    type: 'incident_create',
    label: 'Create Incident/Ticket',
    category: 'security_ops',
    icon: 'Ticket',
    description: 'Create new incident or ticket in ticketing system',
    color: '#ef4444',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'ticketSystem',
        label: 'Ticket System',
        type: 'select',
        required: true,
        options: ['jira', 'servicenow', 'pagerduty', 'custom'],
        helpText: 'Ticketing system to use',
      },
      {
        field: 'title',
        label: 'Title',
        type: 'text',
        required: true,
        placeholder: 'Security Incident: {{alertType}}',
        helpText: 'Incident title (supports templates)',
      },
      {
        field: 'severity',
        label: 'Severity',
        type: 'select',
        required: true,
        options: ['critical', 'high', 'medium', 'low'],
        default: 'high',
        helpText: 'Incident severity',
      },
      {
        field: 'description',
        label: 'Description',
        type: 'textarea',
        required: true,
        placeholder: 'Detailed incident description',
        helpText: 'Full description (supports templates)',
      },
      {
        field: 'assignee',
        label: 'Assignee',
        type: 'text',
        required: false,
        placeholder: 'user@company.com or team name',
        helpText: 'Initial assignee',
      },
      {
        field: 'tags',
        label: 'Tags',
        type: 'text',
        required: false,
        placeholder: 'security, automated, critical',
        helpText: 'Comma-separated tags',
      },
    ],
    outputSchema: {
      ticketId: 'string',
      ticketUrl: 'string',
      title: 'string',
      severity: 'string',
      status: 'string',
      createdAt: 'ISO8601',
    },
    tags: ['incident', 'ticketing', 'response'],
  },

  incident_update: {
    type: 'incident_update',
    label: 'Update Incident',
    category: 'security_ops',
    icon: 'Edit',
    description: 'Update existing incident or ticket',
    color: '#ef4444',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'incidentId',
        label: 'Incident ID',
        type: 'text',
        required: true,
        placeholder: 'INCIDENT-12345 or {{incidentId}}',
        helpText: 'Incident/ticket ID to update',
      },
      {
        field: 'status',
        label: 'Status',
        type: 'select',
        required: false,
        options: ['open', 'in_progress', 'on_hold', 'resolved', 'closed'],
        helpText: 'Update incident status',
      },
      {
        field: 'severity',
        label: 'Severity',
        type: 'select',
        required: false,
        options: ['critical', 'high', 'medium', 'low'],
        helpText: 'Update severity level',
      },
      {
        field: 'comment',
        label: 'Comment',
        type: 'textarea',
        required: false,
        placeholder: 'Update message or investigation findings',
        helpText: 'Add comment/note to incident',
      },
      {
        field: 'assignee',
        label: 'Reassign To',
        type: 'text',
        required: false,
        placeholder: 'user@company.com',
        helpText: 'Reassign to different owner',
      },
    ],
    outputSchema: {
      incidentId: 'string',
      updated: 'boolean',
      status: 'string',
      severity: 'string',
      updatedAt: 'ISO8601',
    },
    tags: ['incident', 'ticketing', 'response'],
  },

  evidence_collect: {
    type: 'evidence_collect',
    label: 'Collect Evidence',
    category: 'security_ops',
    icon: 'Package',
    description: 'Collect forensic evidence from systems',
    color: '#ef4444',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'evidenceType',
        label: 'Evidence Type',
        type: 'select',
        required: true,
        options: ['logs', 'memory', 'disk', 'network', 'process', 'registry'],
        helpText: 'Type of evidence to collect',
      },
      {
        field: 'target',
        label: 'Target System',
        type: 'text',
        required: true,
        placeholder: 'hostname or IP',
        helpText: 'System to collect evidence from',
      },
      {
        field: 'timeRange',
        label: 'Time Range',
        type: 'text',
        required: false,
        placeholder: '1h, 24h, 7d',
        helpText: 'Time range for evidence collection',
      },
      {
        field: 'filters',
        label: 'Filters',
        type: 'json',
        required: false,
        placeholder: '{"logLevel": "error", "source": "auth"}',
        helpText: 'Filter criteria for evidence',
      },
      {
        field: 'hashEvidence',
        label: 'Hash Evidence',
        type: 'boolean',
        required: false,
        default: true,
        helpText: 'Calculate cryptographic hash of evidence',
      },
    ],
    outputSchema: {
      evidenceId: 'string',
      evidenceType: 'string',
      target: 'string',
      collectedAt: 'ISO8601',
      itemCount: 'integer',
      dataSize: 'string',
      hash: 'string',
      storageLocation: 'string',
    },
    tags: ['forensics', 'evidence', 'investigation'],
  },

  containment_action: {
    type: 'containment_action',
    label: 'Containment Action',
    category: 'security_ops',
    icon: 'Shield',
    description: 'Execute containment or remediation action',
    color: '#ef4444',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'action',
        label: 'Action',
        type: 'select',
        required: true,
        options: ['block_ip', 'disable_account', 'isolate_host', 'quarantine_file', 'revoke_token'],
        helpText: 'Type of containment action',
      },
      {
        field: 'target',
        label: 'Target',
        type: 'text',
        required: true,
        placeholder: 'IP, user, hostname, file path, token ID',
        helpText: 'Target of containment action',
      },
      {
        field: 'duration',
        label: 'Duration',
        type: 'text',
        required: false,
        placeholder: '1h, 24h, permanent',
        default: 'permanent',
        helpText: 'Duration of containment action',
      },
      {
        field: 'reason',
        label: 'Reason',
        type: 'textarea',
        required: true,
        placeholder: 'Justification for action',
        helpText: 'Audit trail reason for action',
      },
      {
        field: 'requiresApproval',
        label: 'Requires Approval',
        type: 'boolean',
        required: false,
        default: true,
        helpText: 'Require human approval before execution',
      },
    ],
    outputSchema: {
      actionId: 'string',
      action: 'string',
      target: 'string',
      status: 'string',
      executedAt: 'ISO8601',
      duration: 'string',
      result: 'object',
    },
    tags: ['containment', 'remediation', 'response'],
  },

  user_lookup: {
    type: 'user_lookup',
    label: 'User Lookup',
    category: 'security_ops',
    icon: 'Users',
    description: 'Lookup user from directory (AD, Okta, etc)',
    color: '#ef4444',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'identifier',
        label: 'Identifier',
        type: 'select',
        required: true,
        options: ['email', 'userId', 'employeeId', 'username'],
        helpText: 'User identifier type',
      },
      {
        field: 'identifierValue',
        label: 'Value',
        type: 'text',
        required: true,
        placeholder: 'user@company.com or {{userId}}',
        helpText: 'Identifier value to lookup',
      },
      {
        field: 'fields',
        label: 'Return Fields',
        type: 'text',
        required: false,
        placeholder: 'email, manager, department, groups',
        helpText: 'Comma-separated fields to return',
      },
    ],
    outputSchema: {
      userId: 'string',
      email: 'string',
      name: 'string',
      department: 'string',
      manager: 'object',
      groups: 'array',
      attributes: 'object',
      found: 'boolean',
    },
    tags: ['user', 'directory', 'identity'],
  },

  user_action: {
    type: 'user_action',
    label: 'User Management Action',
    category: 'security_ops',
    icon: 'UserX',
    description: 'Execute user management action (disable, reset password, etc)',
    color: '#ef4444',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'action',
        label: 'Action',
        type: 'select',
        required: true,
        options: ['disable', 'enable', 'reset_password', 'revoke_sessions', 'remove_from_groups', 'suspend'],
        helpText: 'User management action to execute',
      },
      {
        field: 'target',
        label: 'Target User',
        type: 'text',
        required: true,
        placeholder: 'user@company.com or {{userId}}',
        helpText: 'User to target (email or ID)',
      },
      {
        field: 'reason',
        label: 'Reason',
        type: 'textarea',
        required: true,
        placeholder: 'Justification for action',
        helpText: 'Audit trail reason for action',
      },
      {
        field: 'groupsToRemove',
        label: 'Groups to Remove',
        type: 'text',
        required: false,
        placeholder: 'group1, group2',
        helpText: 'For remove_from_groups action',
      },
      {
        field: 'duration',
        label: 'Duration',
        type: 'text',
        required: false,
        placeholder: '24h, 7d',
        helpText: 'For temporary suspend action',
      },
      {
        field: 'requiresApproval',
        label: 'Requires Approval',
        type: 'boolean',
        required: false,
        default: true,
        helpText: 'Require human approval before execution',
      },
    ],
    outputSchema: {
      actionId: 'string',
      action: 'string',
      user: 'string',
      status: 'string',
      executedAt: 'ISO8601',
      result: 'object',
    },
    tags: ['user-management', 'identity', 'response'],
  },
};

// ============================================================================
// LOGIC & CONTROL FLOW NODE TYPES (8)
// ============================================================================

const LOGIC_NODES = {
  condition: {
    type: 'condition',
    label: 'Condition',
    category: 'logic',
    icon: 'GitBranch',
    description: 'Evaluate expression and branch true/false',
    color: '#8b5cf6',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'true', label: 'True', color: '#78716c' },
        { name: 'false', label: 'False', color: '#ef4444' },
      ],
    },
    configSchema: [
      {
        field: 'expression',
        label: 'Expression',
        type: 'expression',
        required: true,
        placeholder: 'result.score > 0.8 && result.severity === "high"',
        helpText: 'JavaScript expression evaluated against input data',
      },
    ],
    outputSchema: {
      result: 'boolean',
      matchedBranch: 'string',
      evaluatedAt: 'ISO8601',
    },
    tags: ['branching', 'conditional', 'control'],
  },

  switch_case: {
    type: 'switch_case',
    label: 'Switch / Case',
    category: 'logic',
    icon: 'GitBranch',
    description: 'Multi-way branching based on expression value',
    color: '#8b5cf6',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'case1', label: 'Case 1' },
        { name: 'case2', label: 'Case 2' },
        { name: 'case3', label: 'Case 3' },
        { name: 'default', label: 'Default', color: '#8b5cf6' },
      ],
    },
    configSchema: [
      {
        field: 'expression',
        label: 'Expression',
        type: 'expression',
        required: true,
        placeholder: 'input.severity',
        helpText: 'Expression to evaluate for branching',
      },
      {
        field: 'cases',
        label: 'Cases',
        type: 'json',
        required: true,
        placeholder: '[{"value": "critical", "label": "Case 1"}, {"value": "high", "label": "Case 2"}]',
        helpText: 'Array of case values and labels',
      },
    ],
    outputSchema: {
      evaluatedValue: 'any',
      matchedCase: 'string',
      caseIndex: 'integer',
      evaluatedAt: 'ISO8601',
    },
    tags: ['branching', 'multi-way', 'control'],
  },

  parallel_fork: {
    type: 'parallel_fork',
    label: 'Parallel Fork',
    category: 'logic',
    icon: 'GitBranch',
    description: 'Split execution into parallel branches',
    color: '#8b5cf6',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'branch1', label: 'Branch 1' },
        { name: 'branch2', label: 'Branch 2' },
        { name: 'branch3', label: 'Branch 3' },
      ],
    },
    configSchema: [
      {
        field: 'branches',
        label: 'Branch Definitions',
        type: 'json',
        required: true,
        placeholder: '[{"id": "branch1", "label": "Scan"}, {"id": "branch2", "label": "Enrich"}]',
        helpText: 'Define parallel branches',
      },
    ],
    outputSchema: {
      branchCount: 'integer',
      executionId: 'string',
      forkTime: 'ISO8601',
    },
    tags: ['parallel', 'concurrency', 'control'],
  },

  // Alias: backend emits canvas type `fork` for Conductor FORK_JOIN, FORK_JOIN_DYNAMIC,
  // and DYNAMIC_FORK (see ConductorTaskType.java and CanonicalNodeType.getDefaultCanvasType).
  // Without this entry, React Flow falls back to the generic renderer → skinny
  // unstyled boxes and a mangled Sugiyama layout for every converted external workflow
  // that contains a parallel fork.
  fork: {
    type: 'fork',
    /* RC-109a — paletteHidden added. This is a back-compat alias of
     * parallel_fork used ONLY by the Conductor importer (the BE emits
     * canvas type `fork` when converting FORK_JOIN / FORK_JOIN_DYNAMIC /
     * DYNAMIC_FORK). Operators authoring fresh canvases should use the
     * canonical `parallel_fork` from the palette. Pre-RC-109a the alias
     * showed in the left palette with an identical label, producing two
     * 'Parallel Fork' entries that did the same thing — operator
     * confusion + duplicate-pick bugs. Picker already filters via
     * isAlias-from-aliasOf. */
    paletteHidden: true,
    label: 'Parallel Fork',
    category: 'logic',
    icon: 'GitBranch',
    description: 'Split execution into parallel branches. Converted from Conductor FORK_JOIN / FORK_JOIN_DYNAMIC / DYNAMIC_FORK.',
    color: '#8b5cf6',
    aliasOf: 'parallel_fork',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'branch1', label: 'Branch 1' },
        { name: 'branch2', label: 'Branch 2' },
        { name: 'branch3', label: 'Branch 3' },
      ],
    },
    configSchema: [],
    outputSchema: {
      branchCount: 'integer',
      executionId: 'string',
      forkTime: 'ISO8601',
    },
    tags: ['parallel', 'fork', 'conductor', 'fork_join', 'fork_join_dynamic', 'dynamic_fork'],
  },

  parallel_join: {
    type: 'parallel_join',
    label: 'Parallel Join',
    category: 'logic',
    icon: 'GitBranch',
    description: 'Wait for parallel branches to complete',
    color: '#8b5cf6',
    ports: {
      in: [
        { name: 'branch1', label: 'Branch 1' },
        { name: 'branch2', label: 'Branch 2' },
        { name: 'branch3', label: 'Branch 3' },
      ],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'joinType',
        label: 'Join Type',
        type: 'select',
        required: true,
        options: ['all', 'any', 'N_of_M'],
        default: 'all',
        helpText: 'Wait for all branches, any branch, or N of M',
      },
      {
        field: 'N',
        label: 'N (for N_of_M)',
        type: 'number',
        required: false,
        placeholder: '2',
        helpText: 'Number of branches required to complete',
      },
      {
        field: 'timeout',
        label: 'Timeout (seconds)',
        type: 'number',
        required: false,
        default: 300,
        helpText: 'Maximum time to wait for branches',
      },
      {
        field: 'onTimeout',
        label: 'On Timeout',
        type: 'select',
        required: false,
        options: ['fail', 'continue', 'proceed_with_completed'],
        default: 'fail',
        helpText: 'Action when timeout occurs',
      },
    ],
    outputSchema: {
      completedBranches: 'array',
      failedBranches: 'array',
      joinTime: 'ISO8601',
      totalDuration: 'number',
      results: 'object',
    },
    tags: ['parallel', 'synchronization', 'control'],
  },

  // Alias: backend emits canvas type `join` for Conductor JOIN / PARALLEL_JOIN
  // (see ConductorTaskType.java line 23 and CanonicalNodeType.getDefaultCanvasType).
  // Without this entry, the join node — which closes every fork in a converted
  // external workflow — renders as an unstyled generic box and the DAG layout
  // collapses around it.
  join: {
    type: 'join',
    /* RC-109a — paletteHidden added. Same situation as the `fork` alias
     * above — back-compat alias of parallel_join used by the Conductor
     * importer for JOIN / PARALLEL_JOIN. Pre-RC-109a, two 'Parallel
     * Join' entries showed in the left palette. */
    paletteHidden: true,
    label: 'Parallel Join',
    category: 'logic',
    icon: 'GitBranch',
    description: 'Wait for parallel branches to complete. Converted from Conductor JOIN.',
    color: '#8b5cf6',
    aliasOf: 'parallel_join',
    ports: {
      in: [
        { name: 'branch1', label: 'Branch 1' },
        { name: 'branch2', label: 'Branch 2' },
        { name: 'branch3', label: 'Branch 3' },
      ],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [],
    outputSchema: {
      completedBranches: 'array',
      failedBranches: 'array',
      joinTime: 'ISO8601',
      totalDuration: 'number',
      results: 'object',
    },
    tags: ['parallel', 'join', 'conductor', 'synchronization'],
  },

  loop: {
    type: 'loop',
    label: 'Loop (single-pass)',
    category: 'logic',
    icon: 'RotateCw',
    // Pre-launch (5/15) semantics — the runtime evaluates the loop
    // condition once and routes to "body" (truthy) or "done" (falsy).
    // True multi-iteration re-execution is post-launch work; today this
    // node behaves as a one-shot conditional with loop-flavoured ports.
    description: 'Evaluates a condition once and routes body/done. True multi-iteration is post-launch.',
    color: '#8b5cf6',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'body', label: 'Body (cond=true)' },
        { name: 'done', label: 'Done (cond=false)' },
      ],
    },
    configSchema: [
      {
        field: 'collection',
        label: 'Collection',
        type: 'expression',
        required: true,
        placeholder: 'input.items or {{alerts}}',
        helpText: 'Collection/array to iterate',
      },
      {
        field: 'itemVariable',
        label: 'Item Variable',
        type: 'text',
        required: true,
        placeholder: 'item',
        default: 'item',
        helpText: 'Variable name for current item',
      },
      {
        field: 'indexVariable',
        label: 'Index Variable',
        type: 'text',
        required: false,
        placeholder: 'index',
        helpText: 'Variable name for current index',
      },
      {
        field: 'maxIterations',
        label: 'Max Iterations',
        type: 'number',
        required: false,
        default: 1000,
        helpText: 'Safety limit on iterations',
      },
    ],
    outputSchema: {
      itemCount: 'integer',
      iterationNumber: 'integer',
      currentItem: 'any',
      currentIndex: 'integer',
      results: 'array',
    },
    tags: ['iteration', 'loop', 'control'],
  },

  delay: {
    type: 'delay',
    label: 'Delay / Wait',
    category: 'wait_signal',
    icon: 'Clock',
    description: 'Wait for specified time or until date/time',
    color: '#8b5cf6',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'delayType',
        label: 'Delay Type',
        type: 'select',
        required: true,
        options: ['fixed', 'until'],
        default: 'fixed',
        helpText: 'Fixed duration or until specific time',
      },
      {
        field: 'seconds',
        label: 'Seconds',
        type: 'number',
        required: false,
        placeholder: '300',
        helpText: 'Seconds to wait (for fixed type)',
      },
      {
        field: 'until',
        label: 'Until (ISO8601)',
        type: 'text',
        required: false,
        placeholder: '2024-12-25T09:00:00Z',
        helpText: 'Wait until this timestamp',
      },
    ],
    outputSchema: {
      delayDuration: 'number',
      startTime: 'ISO8601',
      endTime: 'ISO8601',
      actualDuration: 'number',
    },
    tags: ['timing', 'sleep', 'control'],
  },

  sub_workflow: {
    type: 'sub_workflow',
    label: 'Sub-Workflow',
    category: 'logic',
    icon: 'GitBranch',
    description: 'Invoke another workflow as sub-process',
    color: '#8b5cf6',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'workflowId',
        label: 'Workflow',
        type: 'select',
        required: true,
        options: [],
        helpText: 'Workflow to invoke',
      },
      {
        field: 'input',
        label: 'Input Data',
        type: 'json',
        required: false,
        placeholder: '{"key": "value"}',
        helpText: 'Input to pass to sub-workflow',
      },
      {
        field: 'timeout',
        label: 'Timeout (seconds)',
        type: 'number',
        required: false,
        default: 600,
        helpText: 'Sub-workflow execution timeout',
      },
      {
        field: 'onError',
        label: 'On Error',
        type: 'select',
        required: false,
        options: ['fail', 'continue', 'use_default'],
        default: 'fail',
        helpText: 'Error handling strategy',
      },
    ],
    outputSchema: {
      workflowId: 'string',
      output: 'object',
      success: 'boolean',
      duration: 'number',
      executionId: 'string',
    },
    tags: ['workflow', 'composition', 'control'],
  },

  error_handler: {
    type: 'error_handler',
    label: 'Error Handler',
    category: 'logic',
    icon: 'AlertCircle',
    description: 'Try/catch error handling with retry logic',
    color: '#8b5cf6',
    // Hidden from the palette pre-launch (5/15) — there is no runtime
    // handler for this node type today. Functionally equivalent
    // try/catch + retry semantics already exist as per-node attributes
    // (Attributes tab → retryCount / retryDelay / backoffStrategy)
    // which the runtime DOES enforce. Re-introduce as a discrete node
    // post-launch when we ship a proper exception-flow design (linked
    // catch-block sub-DAG, not just retry attributes).
    paletteHidden: true,
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'success', label: 'Success', color: '#78716c' },
        { name: 'error', label: 'Error', color: '#ef4444' },
      ],
    },
    configSchema: [
      {
        field: 'retryCount',
        label: 'Retry Count',
        type: 'number',
        required: false,
        default: 3,
        helpText: 'Number of retries on error',
      },
      {
        field: 'retryDelay',
        label: 'Retry Delay (ms)',
        type: 'number',
        required: false,
        default: 1000,
        helpText: 'Delay between retries',
      },
      {
        field: 'backoffMultiplier',
        label: 'Backoff Multiplier',
        type: 'number',
        required: false,
        default: 2,
        min: 1,
        helpText: 'Exponential backoff multiplier',
      },
      {
        field: 'fallbackAction',
        label: 'Fallback Action',
        type: 'select',
        required: false,
        options: ['none', 'use_default', 'skip', 'escalate'],
        default: 'escalate',
        helpText: 'Action if all retries fail',
      },
    ],
    outputSchema: {
      success: 'boolean',
      attempts: 'integer',
      error: 'string|object',
      lastError: 'object',
      duration: 'number',
    },
    tags: ['error-handling', 'retry', 'resilience'],
  },

  // ── End / Terminate ────────────────────────────────────────────────
  end: {
    type: 'end',
    label: 'End',
    category: 'logic',
    icon: 'CircleStop',
    description: 'Terminates the workflow with a specific status. Converted from Conductor TERMINATE task.',
    color: '#dc2626',
    /* Phase B-fix.3 — hidden from palette. The end sentinel is auto-appended
     * synthetically at render time (per the synthetic END node in CanvasPageV3's
     * IR-to-display layer); operators should not drop a second one. The
     * Terminate node (defined below) remains palette-visible for the explicit
     * "terminate with status" use case that's actually a runtime task, not a
     * sentinel. See SYSTEM_WORKFLOW_RULES.md §2.6. */
    paletteHidden: true,
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [],
    },
    configSchema: [
      { key: 'terminationStatus', type: 'select', label: 'Termination Status', options: ['COMPLETED', 'FAILED'], default: 'COMPLETED', description: 'Final status of the workflow' },
      { key: 'terminationReason', type: 'text', label: 'Reason', required: false, description: 'Human-readable reason for termination' },
    ],
    outputSchema: {
      terminationStatus: 'string',
      terminationReason: 'string',
      terminatedAt: 'string',
    },
    tags: ['end', 'terminate', 'stop', 'workflow'],
  },

  terminate: {
    type: 'terminate',
    label: 'Terminate',
    category: 'logic',
    icon: 'CircleStop',
    description: 'Early exit point for failure, error, or short-circuit branches. Use instead of routing long connections back to the main End node.',
    color: '#dc2626',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [],
    },
    configSchema: [
      { key: 'terminationStatus', type: 'select', label: 'Termination Status', options: ['COMPLETED', 'FAILED'], default: 'FAILED', description: 'Final status for this branch' },
      { key: 'terminationReason', type: 'text', label: 'Reason', required: false, description: 'Human-readable reason for termination' },
    ],
    outputSchema: {
      terminationStatus: 'string',
      terminationReason: 'string',
    },
    tags: ['terminate', 'stop', 'exit', 'failure', 'error'],
  },

  do_while: {
    type: 'do_while',
    label: 'Do While Loop',
    category: 'logic',
    icon: 'RotateCw',
    description: 'Loop until condition is met. Converted from Conductor DO_WHILE task.',
    color: '#8b5cf6',
    aliasOf: 'loop',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'body', label: 'Body' },
        { name: 'done', label: 'Done' },
      ],
    },
    configSchema: [
      { key: 'loopCondition', type: 'text', label: 'Loop Condition', required: true, description: 'Condition expression — loop continues while true' },
      { key: 'maxIterations', type: 'number', label: 'Max Iterations', default: 10, description: 'Safety limit' },
    ],
    outputSchema: {
      loopConditionResult: 'boolean',
      maxIterations: 'number',
    },
    tags: ['loop', 'do_while', 'iteration', 'conductor'],
  },

  convergence: {
    type: 'convergence',
    /* RC-98e — auto-inserted structural sentinel where switch branches
     * converge. Not operator-authored from the palette; the canvas auto-
     * inserts it when a switch_case is created. Hidden by design, not
     * because it's unfinished. */
    paletteHidden: true,
    label: 'Switch Convergence',
    category: 'logic',
    icon: 'GitMerge',
    description: 'Structural convergence point where switch branches meet',
    color: '#9ca3af',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [],
    outputSchema: {},
    tags: ['convergence', 'switch', 'structural'],
  },

  async_sub_workflow: {
    type: 'async_sub_workflow',
    label: 'Start Workflow (async)',
    category: 'logic',
    icon: 'Send',
    description: 'Fire-and-forget child workflow start. Converted from Conductor START_WORKFLOW.',
    color: '#8b5cf6',
    aliasOf: 'sub_workflow',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      { field: 'workflowName', label: 'Workflow Name', type: 'text', required: true, helpText: 'Child workflow to launch asynchronously' },
      { field: 'workflowVersion', label: 'Version', type: 'number', required: false, helpText: 'Specific version (optional)' },
      { field: 'input', label: 'Input Data', type: 'json', required: false, placeholder: '{"key": "value"}', helpText: 'Input payload passed to the child workflow' },
      { field: 'invocationMode', label: 'Invocation Mode', type: 'select', required: false, options: ['async'], default: 'async', helpText: 'Start mode — async means parent does not wait for the child' },
      { field: 'correlationId', label: 'Correlation ID', type: 'text', required: false, helpText: 'Optional correlation id to track the async child' },
    ],
    outputSchema: {
      workflowId: 'string',
      started: 'boolean',
      correlationId: 'string',
    },
    tags: ['workflow', 'composition', 'async', 'conductor'],
  },

  signal_wait: {
    type: 'signal_wait',
    label: 'Wait for Signal',
    category: 'wait_signal',
    icon: 'Inbox',
    description: 'Suspend workflow until an external signal/event/webhook arrives. Converted from Conductor WAIT_FOR_WEBHOOK / WAIT_FOR_EVENT.',
    color: '#8b5cf6',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'default', label: 'Signal Received' },
        { name: 'timeout', label: 'Timeout', color: '#ef4444' },
      ],
    },
    configSchema: [
      { field: 'signalName', label: 'Signal / Webhook Name', type: 'text', required: true, helpText: 'Named signal, event, or webhook this node waits for' },
      { field: 'signalMatches', label: 'Signal Match Expression', type: 'textarea', required: false, placeholder: '{"type": "payment.completed"}', helpText: 'Optional payload matcher expression' },
      { field: 'waitTimeoutSeconds', label: 'Timeout (seconds)', type: 'number', required: false, default: 3600, helpText: 'Abort and route to timeout port if no signal arrives in time' },
    ],
    outputSchema: {
      signalPayload: 'object',
      receivedAt: 'ISO8601',
      timedOut: 'boolean',
    },
    tags: ['signal', 'wait', 'webhook', 'event', 'conductor'],
  },

  dynamic_task: {
    type: 'dynamic_task',
    /* RC-99 — UNHIDDEN. BE handler is fully wired in
     * CanvasDAGWorkflowImpl (cases "dynamic_task" and "dynamic" at
     * line ~1968, resolveDynamicTaskName helper at line ~2985) with
     * a production-grade resolution-miss diagnostic that names every
     * config source the runtime tried.
     *
     * Also fixed the field-name drift the pre-RC-99 paletteHidden
     * was masking: the schema below now declares
     * `dynamicTaskNameExpression` to match what every BE writer
     * emits (CanvasDAGWorkflowImpl + WorkflowMappingEngine +
     * ConductorAdapter all read/write this exact key). Pre-RC-99 the
     * FE field was `dynamicTaskExpression` — operator-filled values
     * would have landed in a key the runtime ignores, tripping the
     * resolution-miss diagnostic on every run.
     */
    label: 'Dynamic Task',
    category: 'logic',
    icon: 'Shuffle',
    description: 'Single task whose implementation is resolved at runtime. Converted from Conductor DYNAMIC (runtime type dispatch — NOT fan-out).',
    color: '#8b5cf6',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      { field: 'dynamicTaskNameParam', label: 'Task Name Param', type: 'text', required: true, helpText: 'Input parameter that holds the resolved task reference at runtime' },
      /* RC-99 — field name MUST be `dynamicTaskNameExpression` so the
       * value lands in the key CanvasDAGWorkflowImpl + WorkflowMappingEngine
       * + ConductorAdapter all read. A rename here without the corresponding
       * v2Lint LINT_FIELD_REFERENCES update (RC-116a) would be caught
       * at module-init in dev/test. */
      { field: 'dynamicTaskNameExpression', label: 'Resolution Expression', type: 'text', required: false, helpText: 'Expression producing the task name (e.g., ${workflow.input.taskType})' },
      { field: 'input', label: 'Forwarded Input', type: 'json', required: false, placeholder: '{"key": "value"}', helpText: 'Payload forwarded to the resolved task' },
    ],
    outputSchema: {
      resolvedTaskName: 'string',
      result: 'any',
      success: 'boolean',
    },
    tags: ['dynamic', 'runtime', 'dispatch', 'conductor'],
  },
};

// ============================================================================
// SECURITY GATES NODE TYPES (5)
// ============================================================================

const GATE_NODES = {
  // Human Task — the canonical HUMAN-in-the-loop step. Rendered in the
  // Approval Center natively (no Conductor dependency); the form fields
  // declared here are what the operator sees, and their answers become
  // the node's outputData so downstream nodes can reference them via
  // input mapping (e.g. ${take_claim_response.policy_picker} →
  // createClaimForPolicy.policyId). Conversion from a Conductor HUMAN
  // task targets THIS type, not the legacy approval_gate, so imported
  // workflows surface their existing forms unchanged.
  human_task: {
    type: 'human_task',
    label: 'Human Task',
    category: 'gates',
    icon: 'Users',
    description: 'Form-driven human approval — surfaces in the Approval Center with a custom form. The operator’s answers become the task’s output for downstream nodes.',
    color: '#f97316',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'approved', label: 'Approved', color: '#78716c' },
        { name: 'rejected', label: 'Rejected', color: '#ef4444' },
        { name: 'timeout', label: 'Timeout', color: '#a8a29e' },
      ],
    },
    configSchema: [
      {
        field: 'approvers',
        label: 'Approvers',
        type: 'text',
        required: false,
        placeholder: 'user@company.com, @security-team',
        helpText: 'Optional. If empty, routing falls back to the policy assigned to this org/tenant.',
      },
      {
        field: 'urgency',
        label: 'Urgency',
        type: 'select',
        required: false,
        default: 'normal',
        options: [
          { value: 'low', label: 'Low' },
          { value: 'normal', label: 'Normal' },
          { value: 'high', label: 'High' },
          { value: 'critical', label: 'Critical' },
        ],
      },
      {
        field: 'riskLevel',
        label: 'Risk Level',
        type: 'select',
        required: false,
        default: 'medium',
        options: [
          { value: 'low', label: 'Low' },
          { value: 'medium', label: 'Medium' },
          { value: 'high', label: 'High' },
          { value: 'critical', label: 'Critical' },
        ],
      },
      {
        field: 'approvalType',
        label: 'Approval Mode',
        type: 'select',
        required: false,
        default: 'single',
        options: [
          { value: 'single', label: 'Single approver' },
          { value: 'dual', label: 'Dual approval' },
        ],
      },
      {
        field: 'timeout',
        label: 'Timeout (hours)',
        type: 'number',
        required: false,
        default: 24,
        helpText: 'Auto-reject if not approved within timeout.',
      },
      {
        field: 'formSchema',
        label: 'Form Schema (JSON)',
        type: 'textarea',
        required: false,
        placeholder: '{ "fields": [{ "name": "policy_picker", "type": "select", "label": "Pick a policy", "required": true, "options": [...] }, { "name": "incident_description", "type": "textarea", "label": "Describe the incident", "required": true }] }',
        helpText: 'Optional. Describes the form rendered in the Approval Center. Field types: text | select | textarea. If omitted, the runtime synthesizes a form from upstream task output.',
      },
    ],
    outputSchema: {
      approved: 'boolean',
      decidedBy: 'string',
      comment: 'string',
      // formData fields are flattened onto the output — they vary per
      // form, but the operator's answers are always available here by
      // their field name.
    },
    tags: ['approval', 'gate', 'human-in-loop', 'human-task', 'form'],
  },

  // RC-103 — form_task: structured-form variant of human_task.
  //
  // Same BE handler (handleApprovalGate in CanvasDAGWorkflowImpl, routed
  // through "case form_task:" alongside human_task/approval_gate).
  // Same suspension primitive (Workflow.await), same resume signal
  // (submitApprovalDecision). The distinction is operator-facing:
  //   - human_task = free-form approve/reject (formSchema optional)
  //   - form_task  = structured data collection (formSchema REQUIRED)
  //
  // BE persists formSchema onto the ApprovalRequest row and flattens
  // the submitted formData into the node's outputData, so downstream
  // nodes read each field as `${nodeId}.fieldName` directly.
  //
  // form_task is CanonicalNodeType.FORM_RENDER on the IR side
  // (CanonicalNodeType.java:213). No new enum, no new activity — the
  // shared handler path is the whole BE story.
  //
  // NOTE on rendering: full structured-form rendering in the Approval
  // Center is tracked separately. Today the BE persists submitted
  // formData when the FE sends it via /approve {formData: {...}}; the
  // ApprovalCenter form-input UI is a follow-up RC. Operators can
  // drive form_task end-to-end via the API right now.
  form_task: {
    type: 'form_task',
    label: 'Form Task',
    category: 'gates',
    icon: 'ClipboardEdit',
    description: 'Mid-flow structured form collection — workflow pauses, operator fills the schema-driven form in the Approval Center, form fields become this node\'s output (${nodeId}.<fieldName>). For free-form approve/reject use human_task instead.',
    color: '#f97316',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'approved', label: 'Submitted', color: '#78716c' },
        { name: 'rejected', label: 'Rejected', color: '#ef4444' },
        { name: 'timeout', label: 'Timeout', color: '#a8a29e' },
      ],
    },
    configSchema: [
      {
        field: 'formSchema',
        label: 'Form Schema (JSON)',
        type: 'textarea',
        required: true,
        placeholder: '{\n  "fields": [\n    { "name": "incidentSummary", "type": "textarea", "label": "Summarise the incident", "required": true },\n    { "name": "severity", "type": "select", "label": "Severity", "required": true, "options": ["low","medium","high","critical"] },\n    { "name": "affectedAssetCount", "type": "number", "label": "Affected assets" }\n  ]\n}',
        helpText: 'REQUIRED. The form to render in the Approval Center. Each field becomes a key on this node\'s output. Supported field types: text | textarea | select | number | boolean.',
      },
      {
        field: 'assignees',
        label: 'Assignees',
        type: 'text',
        required: false,
        placeholder: 'user@company.com, @security-team',
        helpText: 'Optional. Who can fill the form. If empty, routing falls back to the org/tenant\'s default approver policy.',
      },
      {
        field: 'title',
        label: 'Task Title (for Approval Center)',
        type: 'text',
        required: false,
        placeholder: 'Triage incident ${trigger-1_output.alertId}',
        helpText: 'Shown as the row title in the Approval Center. Supports ${...} variable interpolation.',
      },
      {
        field: 'instructions',
        label: 'Instructions for the operator',
        type: 'textarea',
        required: false,
        placeholder: 'Review the upstream alert details and complete the triage form below.',
        helpText: 'Optional. Shown above the form fields. Supports markdown.',
      },
      {
        field: 'urgency',
        label: 'Urgency',
        type: 'select',
        required: false,
        default: 'normal',
        options: [
          { value: 'low', label: 'Low' },
          { value: 'normal', label: 'Normal' },
          { value: 'high', label: 'High' },
          { value: 'critical', label: 'Critical' },
        ],
      },
      {
        field: 'timeout',
        label: 'Timeout (hours)',
        type: 'number',
        required: false,
        default: 24,
        helpText: 'Workflow takes the `timeout` branch if the form isn\'t submitted within this window. Use Infinity / -1 for no timeout (not recommended).',
      },
    ],
    outputSchema: {
      submitted: 'boolean',
      submittedBy: 'string',
      submittedAt: 'ISO8601',
      // Each field declared in formSchema is flattened onto this output
      // by its `name`. E.g. a schema field `severity` becomes
      // `${nodeId}.severity` downstream.
    },
    tags: ['form', 'gate', 'human-in-loop', 'structured-input', 'approval-center'],
  },

  approval_gate: {
    type: 'approval_gate',
    label: 'Approval Gate (legacy)',
    category: 'gates',
    icon: 'CheckCircle',
    description: 'Simple approve/reject gate without a form. Prefer Human Task for new flows — Approval Gate is kept for backwards compatibility with workflows authored before the form-driven Human Task node existed.',
    color: '#f97316',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'approved', label: 'Approved', color: '#78716c' },
        { name: 'rejected', label: 'Rejected', color: '#ef4444' },
        { name: 'timeout', label: 'Timeout', color: '#a8a29e' },
      ],
    },
    configSchema: [
      {
        field: 'approvers',
        label: 'Approvers',
        type: 'text',
        required: true,
        placeholder: 'user@company.com, @security-team',
        helpText: 'Comma-separated approvers (any can approve)',
      },
      {
        field: 'message',
        label: 'Approval Message',
        type: 'textarea',
        required: true,
        placeholder: 'Please review and approve this action: {{action}}',
        helpText: 'Message sent to approvers',
      },
      {
        field: 'timeout',
        label: 'Timeout (hours)',
        type: 'number',
        required: false,
        default: 24,
        helpText: 'Auto-reject if not approved within timeout',
      },
      {
        field: 'escalation',
        label: 'Escalation Path',
        type: 'text',
        required: false,
        placeholder: 'manager, director, ciso',
        helpText: 'Escalation chain if primary approver timeout',
      },
    ],
    outputSchema: {
      approvalId: 'string',
      approved: 'boolean',
      approver: 'string',
      decision: 'string',
      reason: 'string',
      decidedAt: 'ISO8601',
    },
    tags: ['approval', 'gate', 'human-in-loop'],
  },

  dual_approval: {
    type: 'dual_approval',
    label: 'Dual Approval',
    category: 'gates',
    icon: 'CheckCircle2',
    description: 'Require approval from two or more specific approvers',
    color: '#f97316',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'approved', label: 'Approved', color: '#78716c' },
        { name: 'rejected', label: 'Rejected', color: '#ef4444' },
      ],
    },
    configSchema: [
      {
        field: 'approvers',
        label: 'Required Approvers',
        type: 'text',
        required: true,
        placeholder: 'user1@company.com, user2@company.com',
        helpText: 'Comma-separated approvers (all must approve)',
      },
      {
        field: 'message',
        label: 'Approval Message',
        type: 'textarea',
        required: true,
        placeholder: 'Please review and approve: {{action}}',
        helpText: 'Message sent to approvers',
      },
      {
        field: 'timeout',
        label: 'Timeout (hours)',
        type: 'number',
        required: false,
        default: 24,
        helpText: 'Auto-reject if not all approved within timeout',
      },
    ],
    outputSchema: {
      approvalId: 'string',
      approved: 'boolean',
      approvals: 'array',
      rejections: 'array',
      decidedAt: 'ISO8601',
    },
    tags: ['approval', 'gate', 'human-in-loop', 'compliance'],
  },

  rbac_gate: {
    type: 'rbac_gate',
    label: 'RBAC Gate',
    category: 'gates',
    icon: 'Lock',
    description: 'Check user role or permission before proceeding',
    color: '#f97316',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'authorized', label: 'Authorized', color: '#78716c' },
        { name: 'denied', label: 'Denied', color: '#ef4444' },
      ],
    },
    configSchema: [
      {
        field: 'requiredRole',
        label: 'Required Role',
        type: 'text',
        required: false,
        placeholder: 'security_admin, incident_responder',
        helpText: 'Required role (from input.user.role)',
      },
      {
        field: 'requiredPermission',
        label: 'Required Permission',
        type: 'text',
        required: false,
        placeholder: 'incident:create, user:disable',
        helpText: 'Required permission (from user attributes)',
      },
      {
        field: 'checkType',
        label: 'Check Type',
        type: 'select',
        required: true,
        options: ['any', 'all'],
        default: 'all',
        helpText: 'Require all or any of specified role/permission',
      },
    ],
    outputSchema: {
      authorized: 'boolean',
      userRole: 'string',
      userPermissions: 'array',
      requiredRole: 'string',
      requiredPermission: 'string',
      denialReason: 'string',
    },
    tags: ['authorization', 'rbac', 'access-control'],
  },

  risk_score_gate: {
    type: 'risk_score_gate',
    label: 'Risk Score Gate',
    category: 'gates',
    icon: 'TrendingDown',
    description: 'Gate based on risk score threshold',
    color: '#f97316',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'pass', label: 'Pass', color: '#78716c' },
        { name: 'fail', label: 'Fail', color: '#ef4444' },
      ],
    },
    configSchema: [
      {
        field: 'threshold',
        label: 'Risk Threshold',
        type: 'number',
        required: true,
        default: 0.7,
        min: 0,
        max: 1,
        step: 0.1,
        helpText: 'Risk score threshold (0-1)',
      },
      {
        field: 'riskExpression',
        label: 'Risk Score Expression',
        type: 'expression',
        required: false,
        placeholder: 'input.riskScore or calculated_risk',
        helpText: 'Expression to calculate or get risk score',
      },
      {
        field: 'riskFactors',
        label: 'Risk Factors',
        type: 'text',
        required: false,
        placeholder: 'severity, impact, exploitability',
        helpText: 'Factors considered in risk calculation',
      },
      {
        field: 'scoringModel',
        label: 'Scoring Model',
        type: 'select',
        required: false,
        options: ['cvss', 'custom', 'machine_learning'],
        default: 'custom',
        helpText: 'Model used to calculate risk',
      },
    ],
    outputSchema: {
      riskScore: 'number',
      threshold: 'number',
      passed: 'boolean',
      factors: 'object',
      scoringModel: 'string',
    },
    tags: ['risk', 'gate', 'scoring'],
  },

  time_window_gate: {
    type: 'time_window_gate',
    label: 'Time Window Gate',
    category: 'gates',
    icon: 'Clock',
    description: 'Gate based on business hours or time window',
    color: '#f97316',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'in_window', label: 'In Window', color: '#78716c' },
        { name: 'out_of_window', label: 'Out of Window', color: '#f97316' },
      ],
    },
    configSchema: [
      {
        field: 'timezone',
        label: 'Timezone',
        type: 'select',
        required: true,
        options: [
          'UTC',
          'America/New_York',
          'America/Chicago',
          'Europe/London',
          'Asia/Tokyo',
        ],
        default: 'UTC',
        helpText: 'Timezone for window calculation',
      },
      {
        field: 'windows',
        label: 'Time Windows',
        type: 'json',
        required: true,
        placeholder: '[{"days": "1-5", "startTime": "09:00", "endTime": "17:00"}]',
        helpText: 'Array of allowed time windows',
      },
      {
        field: 'onOutOfWindow',
        label: 'Out of Window Action',
        type: 'select',
        required: false,
        options: ['fail', 'queue', 'notify'],
        default: 'fail',
        helpText: 'Action when outside time window',
      },
    ],
    outputSchema: {
      inWindow: 'boolean',
      currentTime: 'ISO8601',
      nextWindowStart: 'ISO8601',
      windowName: 'string',
    },
    tags: ['scheduling', 'business-hours', 'gate'],
  },
};

// ============================================================================
// DATA OPERATIONS NODE TYPES (5)
// ============================================================================

const DATA_NODES = {
  variable_set: {
    type: 'variable_set',
    label: 'Set Variable',
    category: 'data',
    icon: 'Database',
    description: 'Set or update a workflow variable',
    color: '#06b6d4',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'variableName',
        label: 'Variable Name',
        type: 'text',
        required: true,
        placeholder: 'my_variable',
        helpText: 'Name of variable to set',
      },
      {
        field: 'value',
        label: 'Value',
        type: 'json',
        required: true,
        placeholder: '"value" or {{input.field}} or 123',
        helpText: 'Value to set (supports templates and expressions)',
      },
      {
        field: 'scope',
        label: 'Scope',
        type: 'select',
        required: true,
        options: ['workflow', 'global'],
        default: 'workflow',
        helpText: 'Variable scope (workflow-local or global)',
      },
    ],
    outputSchema: {
      variableName: 'string',
      previousValue: 'any',
      newValue: 'any',
      scope: 'string',
      timestamp: 'ISO8601',
    },
    tags: ['variable', 'data', 'state'],
  },

  data_transform: {
    type: 'data_transform',
    label: 'Transform Data',
    category: 'data',
    icon: 'Zap',
    description: 'Extract and transform data from upstream nodes using variable assignments',
    color: '#06b6d4',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'assignments',
        label: 'Variable Assignments',
        type: 'assignments',
        required: false,
        helpText: 'Extract values from upstream node outputs. Each assignment creates a named variable for downstream use. Expression syntax: ${nodeId_response.result.fieldName}',
      },
      {
        field: 'outputVariable',
        label: 'Output Variable Name',
        type: 'text',
        required: false,
        placeholder: 'extracted_data',
        helpText: 'Name for this node\'s combined output (defaults to node ID)',
      },
      {
        field: 'transformType',
        label: 'Transform Type',
        type: 'select',
        required: false,
        // RC-DataTransform-Engines — ALL FIVE engines are now wired
        // through the BE (DAGNodeActivitiesImpl.executeDataTransform
        // + executeDataTransform{JsonPath,JmesPath,Regex,Template}).
        // Each is sandbox-by-construction (no eval, no I/O, no host
        // access) and has a compiled-query cache mirroring JQ.
        options: [
          { value: '',         label: '(none — use Variable Assignments above)' },
          { value: 'jq',       label: 'JQ — safe pure-functional projection (recommended)' },
          { value: 'jsonpath', label: 'JSONPath — declarative JSON path queries' },
          { value: 'jmespath', label: 'JMESPath — JSON query with built-in functions' },
          { value: 'regex',    label: 'Regex — RE2 capture-group extraction (linear-time)' },
          { value: 'template', label: 'Template — Pebble template rendering' },
        ],
        helpText: 'Pick the engine that fits your data-shaping need. JQ is the recommended default (most expressive functional library); JSONPath/JMESPath for declarative path queries; Regex for capture-group extraction (RE2, no ReDoS); Template for string rendering (Pebble, sandbox-safe). Leave blank to use the Variable Assignments path above.',
      },
      {
        field: 'expression',
        label: 'Expression',
        type: 'textarea',
        required: false,
        placeholder: '.policies | map({"const": .policy_number, "title": .policy_type})',
        helpText: 'For JQ: a JQ expression evaluated against this node\'s input. The input is exposed as `.` — e.g. `.foo.bar`, `.items | map(select(.active))`, `.policies | map({"const": .policy_number, "title": .policy_type})`. See https://jqlang.github.io/jq/manual/ for the full language reference.',
      },
    ],
    outputSchema: {
      original: 'any',
      transformed: 'any',
      assignments: 'object',
    },
    tags: ['transform', 'data', 'manipulation', 'extract'],
  },

  data_aggregate: {
    type: 'data_aggregate',
    label: 'Aggregate Data',
    category: 'data',
    icon: 'Combine',
    description: 'Aggregate or merge multiple data sources',
    color: '#06b6d4',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'sources',
        label: 'Data Sources',
        type: 'json',
        required: true,
        placeholder: '[{"key": "alerts", "path": "input.alerts"}, {"key": "enrichment", "path": "enrichment_result"}]',
        helpText: 'Array of data sources with keys and paths',
      },
      {
        field: 'mergeStrategy',
        label: 'Merge Strategy',
        type: 'select',
        required: true,
        options: ['merge', 'concat', 'pick', 'nested'],
        default: 'merge',
        helpText: 'How to combine sources',
      },
      {
        field: 'outputKey',
        label: 'Output Key',
        type: 'text',
        required: false,
        placeholder: 'aggregated_data',
        helpText: 'Name for aggregated output object',
      },
    ],
    outputSchema: {
      aggregated: 'object',
      sourceCount: 'integer',
      mergeStrategy: 'string',
    },
    tags: ['aggregation', 'data', 'merge'],
  },

  data_deduplicate: {
    type: 'data_deduplicate',
    label: 'Deduplicate Data',
    category: 'data',
    icon: 'Copy',
    description: 'Remove duplicate items from array',
    color: '#06b6d4',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'input',
        label: 'Input Array',
        type: 'expression',
        required: true,
        placeholder: 'input.items or {{results}}',
        helpText: 'Array to deduplicate',
      },
      {
        field: 'keyField',
        label: 'Key Field(s)',
        type: 'text',
        required: true,
        placeholder: 'id or ip,port',
        helpText: 'Field(s) to use for deduplication',
      },
      {
        field: 'strategy',
        label: 'Strategy',
        type: 'select',
        required: true,
        options: ['first', 'last', 'merge'],
        default: 'first',
        helpText: 'Which duplicate to keep',
      },
    ],
    outputSchema: {
      original: 'array',
      deduplicated: 'array',
      removedCount: 'integer',
      keyField: 'string',
    },
    tags: ['deduplication', 'data', 'cleanup'],
  },

  data_enrich: {
    type: 'data_enrich',
    label: 'Enrich Data',
    category: 'data',
    icon: 'Plus',
    description: 'Enrich data with additional information',
    color: '#06b6d4',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'baseData',
        label: 'Base Data',
        type: 'expression',
        required: true,
        placeholder: 'input.alert',
        helpText: 'Data to enrich',
      },
      {
        field: 'enrichmentSources',
        label: 'Enrichment Sources',
        type: 'json',
        required: true,
        placeholder: '[{"source": "threat_intel", "joinKey": "ip", "targetKey": "enriched_intel"}]',
        helpText: 'Array of enrichment sources with join details',
      },
      {
        field: 'mergeStrategy',
        label: 'Merge Strategy',
        type: 'select',
        required: false,
        options: ['shallow', 'deep'],
        default: 'deep',
        helpText: 'How to merge enriched data',
      },
    ],
    outputSchema: {
      original: 'object',
      enriched: 'object',
      enrichmentSources: 'array',
    },
    tags: ['enrichment', 'data', 'context'],
  },
};

// ============================================================================
// AGENT & SKILL NODE TYPES (2 additional)
// ============================================================================

const AGENT_NODES = {
  agent_chat: {
    type: 'agent_chat',
    label: 'Agent Chat',
    category: 'agents',
    icon: 'MessageCircle',
    description: 'Interactive multi-turn conversation with agent — execute skills with upstream data',
    color: '#78716c',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'agentId',
        label: 'Agent',
        type: 'select',
        required: true,
        options: [],
        helpText: 'Select agent for conversation',
      },
      {
        field: 'selectedSkill',
        label: 'Execute Skill',
        type: 'select',
        required: false,
        options: [],
        helpText: 'Select a skill to execute (optional — agent can auto-select)',
      },
      {
        field: 'promptTemplate',
        label: 'Prompt Template',
        type: 'agent_prompt_builder',
        required: true,
        placeholder: 'Analyze the threat intelligence data and recommend containment actions',
        helpText: 'Build a prompt using upstream variables. Use ${variableName} to reference data from previous nodes.',
      },
      {
        field: 'maxTurns',
        label: 'Max Conversation Turns',
        type: 'number',
        required: false,
        default: 5,
        helpText: 'Maximum conversation turns before returning result',
      },
      {
        field: 'contextSize',
        label: 'Context Window',
        type: 'number',
        required: false,
        default: 10,
        helpText: 'Number of previous messages to include as context',
      },
      {
        field: 'outputVariable',
        label: 'Output Variable Name',
        type: 'text',
        required: false,
        default: 'agent_response',
        helpText: 'Variable name for this node\'s output (available as ${name} downstream)',
      },
    ],
    outputSchema: {
      agentId: 'string',
      skillExecuted: 'string',
      conversationId: 'string',
      turns: 'integer',
      messages: 'array',
      finalResponse: 'string',
      result: 'object',
      duration: 'number',
      outputVariable: 'string',
    },
    tags: ['agent', 'conversation', 'interactive', 'multi-agent', 'orchestration'],
  },

  skill_eval: {
    type: 'skill_eval',
    label: 'Skill Evaluation',
    category: 'agents',
    icon: 'CheckSquare',
    description: 'Run evaluation suite on skill to validate behavior',
    color: '#78716c',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'agentId',
        label: 'Agent',
        type: 'select',
        required: true,
        options: [],
        helpText: 'Select agent containing skill',
      },
      {
        field: 'skillName',
        label: 'Skill Name',
        type: 'select',
        required: true,
        options: [],
        helpText: 'Skill to evaluate',
      },
      {
        field: 'evalSuite',
        label: 'Evaluation Suite',
        type: 'select',
        required: true,
        options: [],
        helpText: 'Pre-defined evaluation suite',
      },
      {
        field: 'threshold',
        label: 'Pass Threshold (%)',
        type: 'number',
        required: false,
        default: 80,
        min: 0,
        max: 100,
        helpText: 'Minimum score to pass evaluation',
      },
    ],
    outputSchema: {
      skillName: 'string',
      evalSuite: 'string',
      testCount: 'integer',
      passCount: 'integer',
      failCount: 'integer',
      score: 'number',
      passed: 'boolean',
      results: 'array',
    },
    tags: ['evaluation', 'testing', 'skill'],
  },
};

// ============================================================================
// CONNECTOR NODE TYPES — Dynamically populated from backend registry
// ============================================================================
//
// Previously this module imported a hardcoded MARKETPLACE_CONNECTORS constant
// at import time. As part of M1 Unification the single source of truth became
// the backend ConnectorRegistryService (served via /api/v1/connector-registry).
//
// `registerConnectorRegistry(connectors, categories)` below is called by
// `src/data/marketplaceRegistry.js` whenever a fresh catalogue arrives. It
// mutates NODE_TYPES in place and notifies subscribers so mounted palettes
// re-render.

import { getCategoryStyle } from './marketplaceCategoryStyle';

/**
 * Build a canvas node type from a marketplace connector definition.
 * Each connector becomes a draggable palette item with its skills as operations.
 *
 * @param connector legacy-shaped connector (see marketplaceFromRegistry.js)
 * @param categoryById optional lookup map {id → {color, ...}}; falls back to
 *                     the static color style map when the id is unknown.
 */
function buildConnectorNode(connector, categoryById = null) {
  // Create a stable type key from the connector id: 'servicenow-v1' → 'connector_servicenow_v1'
  const typeKey = 'connector_' + connector.id.replace(/-/g, '_');

  // Derive skill options from the connector's skills array
  const skillOptions = (connector.skills || []).map((s) => ({
    value: s.id,
    label: `${s.name} (${s.method} ${s.endpoint})`,
  }));

  // Get category color: prefer live catalogue, fall back to the static style
  // registry, then to pink as a last resort.
  const catDef = categoryById ? categoryById[connector.marketplaceCategory] : null;
  const color = catDef?.color
    || getCategoryStyle(connector.marketplaceCategory).color
    || '#ec4899';

  // Build config schema. We deliberately mirror the base `connector_task`
  // schema shape (same field names: connectorId / action / parameters)
  // rather than emitting marketplace-specific names. Why: the InputTab's
  // dynamic-options logic and the rich per-action parameter panel both
  // key off the canonical field names. Diverging here meant marketplace-
  // templated nodes lost the installed-connector dropdown and the per-
  // skill 8-field rendering that base connector_task nodes get for free.
  //
  // The marketplace-specific data (skill list with params, vendor metadata,
  // icon) still rides on `connectorMeta` below so it's available to the
  // renderer for label/icon/colour decisions, but the schema field
  // identifiers and types match what InputTab expects on the canonical
  // path.
  const configSchema = [
    {
      field: 'connectorId',
      label: 'Connector',
      type: 'select',                 // was: 'text' readOnly — operator
      required: true,                  // must be able to pick an installed
      options: [],                     // instance. InputTab fills options
      defaultValue: connector.id,      // from connectorOptions at render time.
      helpText: `Pick an installed ${connector.name} connector. Marketplace template: ${connector.id}`,
    },
    {
      field: 'action',                 // was: 'skill' — rename so the
      label: 'Action',                 // existing connector_task dropdown
      type: 'select',                  // logic in InputTab fires (lines
      required: true,                  // 8815-8830 keys off field === 'action').
      options: [],                     // InputTab populates from selected
      helpText: `Select which ${connector.name} operation to execute`, // connector's skills via actionOptions.
    },
    {
      field: 'parameters',
      label: 'Parameters',
      type: 'json',
      required: false,
      placeholder: '{"key": "value"}',
      helpText: 'Skill-specific parameters (see skill docs for required fields)',
    },
  ];

  return {
    type: typeKey,
    label: connector.name,
    category: 'connector',
    icon: 'Plug',
    description: connector.description || `Execute ${connector.name} operations`,
    color,
    connectorMeta: {
      marketplaceId: connector.id,
      vendor: connector.vendor,
      version: connector.version,
      authType: connector.authType,
      baseUrl: connector.baseUrl,
      tier: connector.tier,
      category: connector.category,
      marketplaceCategory: connector.marketplaceCategory,
      icon: connector.icon,
      skills: connector.skills || [],
      tags: connector.tags || [],
      docs: connector.docs,
    },
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema,
    outputSchema: {
      connectorId: 'string',
      skill: 'string',
      result: 'object',
      success: 'boolean',
      error: 'string',
    },
    tags: connector.tags || [],
  };
}

// Connector palette entries — populated dynamically by
// `registerConnectorRegistry` once the backend catalogue loads. Starts empty
// so the module has no synchronous dependency on marketplace data.
const CONNECTOR_NODES = {};

// Subscriber set so mounted palettes can re-render when entries change.
const _nodeTypeListeners = new Set();

function _notifyNodeTypesChanged() {
  _nodeTypeListeners.forEach((fn) => {
    try { fn(); } catch (e) { /* subscriber failure is isolated */ }
  });
}

/**
 * Rebuild the connector palette from a backend catalogue. Mutates
 * CONNECTOR_NODES (and therefore NODE_TYPES, which spreads it at the end of
 * this module) in place, then notifies subscribers.
 *
 * Idempotent — safe to call repeatedly when the catalogue refreshes.
 */
export function registerConnectorRegistry(connectors, categories) {
  // Build an id→category lookup so each node picks up the live accent color.
  const categoryById = {};
  (Array.isArray(categories) ? categories : []).forEach((c) => {
    if (c && c.id) categoryById[c.id] = c;
  });

  // Clear existing connector entries (keeps NODE_TYPES consistent with the
  // latest catalogue rather than accumulating stale palette items).
  Object.keys(CONNECTOR_NODES).forEach((k) => { delete CONNECTOR_NODES[k]; });
  Object.keys(NODE_TYPES).forEach((k) => {
    if (k.startsWith('connector_')) delete NODE_TYPES[k];
  });

  // Rebuild
  (Array.isArray(connectors) ? connectors : []).forEach((connector) => {
    if (!connector || !connector.id) return;
    const node = buildConnectorNode(connector, categoryById);
    CONNECTOR_NODES[node.type] = node;
    NODE_TYPES[node.type] = node;
  });

  _notifyNodeTypesChanged();
}

/**
 * Subscribe to palette changes. Returns an unsubscribe function. Used by
 * canvas components that render NODE_TYPES to re-render after the registry
 * loads.
 */
export function subscribeNodeTypes(fn) {
  _nodeTypeListeners.add(fn);
  return () => _nodeTypeListeners.delete(fn);
}

// ============================================================================
// ENFORCEMENT & DATA PROTECTION NODE TYPES (7 base types + aliases)
// ============================================================================

const ENFORCEMENT_NODES = {
  // ── Data Protection Gate Scans ─────────────────────────────────────────
  dpg_scan: {
    type: 'dpg_scan',
    label: 'DPG Inbound Scan',
    category: 'enforcement',
    icon: 'ScanSearch',
    description:
      'Scans inbound data for PII, PHI, credentials, and financial data. ' +
      'Applies REDACT, TOKENIZE, MASK, or BLOCK actions per classification rules. Sub-10ms.',
    color: '#dc2626',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'default', label: 'Allowed', color: '#22c55e' },
        { name: 'blocked', label: 'Blocked', color: '#ef4444' },
      ],
    },
    configSchema: [
      {
        field: 'connectorType',
        label: 'Connector / Context Type',
        type: 'text',
        required: false,
        placeholder: 'e.g. email, workflow_entry, inter_step',
        helpText: 'Context identifier for the scan — determines which classification rules apply',
      },
      {
        field: 'tenantId',
        label: 'Tenant ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.tenantId}',
        helpText: 'Resolved from trigger or upstream node output',
      },
      {
        field: 'orgId',
        label: 'Org ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.orgId}',
        helpText: 'Resolved from trigger or upstream node output',
      },
      {
        field: 'agentId',
        label: 'Agent ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.agentId}',
        helpText: 'Agent whose data is being scanned',
      },
      {
        field: 'userId',
        label: 'User ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.userId}',
        helpText: 'User who initiated the action',
      },
      {
        field: 'workflowExecutionId',
        label: 'Workflow Execution ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.workflowExecutionId}',
        helpText: 'Execution context for audit correlation',
      },
    ],
    outputSchema: {
      allowed: 'boolean',
      blocked: 'boolean',
      blockReason: 'string',
      mutationCount: 'number',
      scanTimeMs: 'number',
      detectionCount: 'number',
      scanDirection: 'string',
      sanitizedData: 'object',
    },
    tags: ['dpg', 'data-protection', 'pii', 'scan', 'inbound', 'classification'],
  },

  dpg_scan_inbound: {
    type: 'dpg_scan_inbound',
    /* RC-98e — alias of `dpg_scan`. Canonical entry carries the schema;
     * this alias exists only for back-compat with canvases authored before
     * the dpg_scan rename. Hidden so operators don't see the same node
     * twice in the palette. */
    paletteHidden: true,
    label: 'DPG Inbound Scan',
    category: 'enforcement',
    icon: 'ScanSearch',
    description: 'Alias for dpg_scan — scans inbound data for sensitive information.',
    color: '#dc2626',
    aliasOf: 'dpg_scan',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'default', label: 'Allowed', color: '#22c55e' },
        { name: 'blocked', label: 'Blocked', color: '#ef4444' },
      ],
    },
    configSchema: [],  // Use parent dpg_scan schema
    outputSchema: {},
    tags: ['dpg', 'scan', 'inbound', 'alias'],
  },

  data_classification: {
    type: 'data_classification',
    /* RC-98e — alias of `dpg_scan` (legacy name). Canonical entry carries
     * the schema. Hidden to avoid palette duplication; remains available
     * for back-compat in saved canvases. */
    paletteHidden: true,
    label: 'Data Classification',
    category: 'enforcement',
    icon: 'Tags',
    description: 'Classifies data by sensitivity (PII, PHI, financial, credentials). Alias for DPG inbound scan.',
    color: '#dc2626',
    aliasOf: 'dpg_scan',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'default', label: 'Allowed', color: '#22c55e' },
        { name: 'blocked', label: 'Blocked', color: '#ef4444' },
      ],
    },
    configSchema: [],
    outputSchema: {},
    tags: ['dpg', 'classification', 'alias'],
  },

  dpg_scan_outbound: {
    type: 'dpg_scan_outbound',
    label: 'DPG Outbound Scan',
    category: 'enforcement',
    icon: 'ScanLine',
    description:
      'Scans outbound data before it leaves the platform boundary — connectors, LLM calls, APIs. ' +
      'Prevents credential leakage, PII exfiltration, and unauthorized data transfer.',
    color: '#dc2626',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'default', label: 'Allowed', color: '#22c55e' },
        { name: 'blocked', label: 'Blocked', color: '#ef4444' },
      ],
    },
    configSchema: [
      {
        field: 'connectorType',
        label: 'Connector / Context Type',
        type: 'text',
        required: false,
        placeholder: 'e.g. llm_inference, inter_step, email',
        helpText: 'Context identifier — determines which outbound classification rules apply',
      },
      {
        field: 'tenantId',
        label: 'Tenant ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.tenantId}',
      },
      {
        field: 'orgId',
        label: 'Org ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.orgId}',
      },
      {
        field: 'agentId',
        label: 'Agent ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.agentId}',
      },
      {
        field: 'userId',
        label: 'User ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.userId}',
      },
      {
        field: 'workflowExecutionId',
        label: 'Workflow Execution ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.workflowExecutionId}',
      },
    ],
    outputSchema: {
      allowed: 'boolean',
      blocked: 'boolean',
      blockReason: 'string',
      mutationCount: 'number',
      scanTimeMs: 'number',
      detectionCount: 'number',
      scanDirection: 'string',
      sanitizedData: 'object',
    },
    tags: ['dpg', 'data-protection', 'scan', 'outbound', 'exfiltration'],
  },

  // ── Policy Rule Evaluation ─────────────────────────────────────────────
  policy_rule_evaluation: {
    type: 'policy_rule_evaluation',
    label: 'Policy Rule Evaluation',
    category: 'enforcement',
    icon: 'Scale',
    description:
      'Evaluates deterministic policy rules (L1): regex, range, enum, rate-limit, time-window. ' +
      'Sub-5ms, fail-closed. Returns ALLOW with violations/warnings or BLOCK.',
    color: '#dc2626',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'default', label: 'Allowed', color: '#22c55e' },
        { name: 'blocked', label: 'Blocked', color: '#ef4444' },
      ],
    },
    configSchema: [
      {
        field: 'enforcementPoint',
        label: 'Enforcement Point',
        type: 'select',
        required: true,
        options: ['WORKFLOW_ENTRY', 'INTER_STEP', 'CONNECTOR_GUARD', 'HEALTH_ALERT'],
        default: 'WORKFLOW_ENTRY',
        helpText: 'Where in the pipeline this evaluation runs (PEP-1, PEP-2, PEP-3)',
      },
      {
        field: 'agentId',
        label: 'Agent ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.agentId}',
      },
      {
        field: 'skillId',
        label: 'Skill ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.skillId}',
      },
      {
        field: 'skillName',
        label: 'Skill Name',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.skillName}',
      },
      {
        field: 'skillType',
        label: 'Skill Type',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.skillType}',
      },
      {
        field: 'userId',
        label: 'User ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.userId}',
      },
      {
        field: 'orgId',
        label: 'Org ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.orgId}',
      },
      {
        field: 'tenantId',
        label: 'Tenant ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.tenantId}',
      },
      {
        field: 'workflowExecutionId',
        label: 'Workflow Execution ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.workflowExecutionId}',
      },
    ],
    outputSchema: {
      allowed: 'boolean',
      violationCount: 'number',
      warningCount: 'number',
      evaluationTimeMs: 'number',
      enforcementPoint: 'string',
      violations: 'array',
    },
    tags: ['policy', 'rules', 'deterministic', 'l1', 'enforcement'],
  },

  deterministic_rules: {
    type: 'deterministic_rules',
    /* RC-98e — alias of `policy_rule_evaluation`. Canonical entry carries
     * the schema. Hidden to avoid palette duplication; available for
     * back-compat in saved canvases. */
    paletteHidden: true,
    label: 'Deterministic Rules',
    category: 'enforcement',
    icon: 'Scale',
    description: 'Alias for policy_rule_evaluation — L1 deterministic rule engine.',
    color: '#dc2626',
    aliasOf: 'policy_rule_evaluation',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'default', label: 'Allowed', color: '#22c55e' },
        { name: 'blocked', label: 'Blocked', color: '#ef4444' },
      ],
    },
    configSchema: [],
    outputSchema: {},
    tags: ['policy', 'rules', 'l1', 'alias'],
  },

  // ── Policy Enforcement Gate (Full 6-Stage Pipeline) ────────────────────
  policy_enforcement_gate: {
    type: 'policy_enforcement_gate',
    label: 'Policy Enforcement Gate',
    category: 'enforcement',
    icon: 'ShieldAlert',
    description:
      'Full 6-stage enforcement pipeline: Deterministic Rules → Autonomy → System Agent Protection → ' +
      'RBAC → Guardrails → Policy Document RAG. Runs the complete PolicyEnforcementGate.evaluateExecution().',
    color: '#dc2626',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'default', label: 'Allowed', color: '#22c55e' },
        { name: 'blocked', label: 'Blocked', color: '#ef4444' },
      ],
    },
    configSchema: [
      {
        field: 'agentId',
        label: 'Agent ID',
        type: 'text',
        required: true,
        placeholder: '${trigger-1_output.agentId}',
        helpText: 'Agent being evaluated',
      },
      {
        field: 'skillName',
        label: 'Skill Name',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.skillName}',
      },
      {
        field: 'skillId',
        label: 'Skill ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.skillId}',
      },
      {
        field: 'skillType',
        label: 'Skill Type',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.skillType}',
      },
      {
        field: 'userId',
        label: 'User ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.userId}',
      },
      {
        field: 'workflowExecutionId',
        label: 'Workflow Execution ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.workflowExecutionId}',
      },
    ],
    outputSchema: {
      allowed: 'boolean',
      reason: 'string',
      stagesEvaluated: 'number',
      hasPolicyContext: 'boolean',
    },
    tags: ['policy', 'enforcement', 'gate', '6-stage', 'pep'],
  },

  policy_enforcement: {
    type: 'policy_enforcement',
    /* RC-98e — alias of `policy_enforcement_gate`. Canonical entry carries
     * the schema. Hidden to avoid palette duplication. */
    paletteHidden: true,
    label: 'Policy Enforcement',
    category: 'enforcement',
    icon: 'ShieldAlert',
    description: 'Alias for policy_enforcement_gate — full 6-stage enforcement pipeline.',
    color: '#dc2626',
    aliasOf: 'policy_enforcement_gate',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'default', label: 'Allowed', color: '#22c55e' },
        { name: 'blocked', label: 'Blocked', color: '#ef4444' },
      ],
    },
    configSchema: [],
    outputSchema: {},
    tags: ['policy', 'enforcement', 'alias'],
  },

  // ── Tokenized Data Vault ───────────────────────────────────────────────
  token_vault: {
    type: 'token_vault',
    /* RC-98b — unhidden. configSchema (8 fields) is production-ready and
     * BE handler DAGNodeActivities.executeVaultOperation() is fully wired
     * (handles tokenize + resolve, returns ${nodeId}_result). The blanket
     * "V4 Phase 5f-1 not production-ready" tag on this category was wrong
     * for token_vault / audit_event / compliance_scoring — they were
     * complete the whole time. */
    label: 'Tokenized Data Vault',
    category: 'enforcement',
    icon: 'KeyRound',
    description:
      'AES-256-GCM encrypted token vault for sensitive data. Tokenize replaces sensitive values with ' +
      'workflow-scoped tokens (24hr TTL). Resolve recovers the original value within scope.',
    color: '#dc2626',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'action',
        label: 'Vault Action',
        type: 'select',
        required: true,
        options: ['tokenize', 'resolve'],
        default: 'tokenize',
        helpText: 'Tokenize: replace sensitive value with token. Resolve: recover original from token.',
      },
      {
        field: 'category',
        label: 'Data Category',
        type: 'select',
        required: false,
        options: ['PII', 'FINANCIAL', 'CREDENTIAL', 'PHI', 'CUSTOM'],
        default: 'PII',
        helpText: 'Classification category for the tokenized data',
      },
      {
        field: 'sensitiveValue',
        label: 'Sensitive Value (for tokenize)',
        type: 'text',
        required: false,
        placeholder: '${dpg-scan-1_result.sanitizedData}',
        helpText: 'The value to tokenize — typically from a DPG scan result',
      },
      {
        field: 'token',
        label: 'Token (for resolve)',
        type: 'text',
        required: false,
        placeholder: '${vault-1_result.token}',
        helpText: 'Token to resolve back to original value',
      },
      {
        field: 'tenantId',
        label: 'Tenant ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.tenantId}',
      },
      {
        field: 'userId',
        label: 'User ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.userId}',
      },
      {
        field: 'workflowExecutionId',
        label: 'Workflow Execution ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.workflowExecutionId}',
      },
      {
        field: 'connectorType',
        label: 'Connector Type',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.connectorType}',
        helpText: 'Scope context for token isolation',
      },
      {
        field: 'vaultConnectorId',
        label: 'External Vault Connector',
        type: 'select',
        required: false,
        options: [],  // Populated at runtime from installed secrets-vault connectors
        helpText:
          'Optional: select an external vault connector (HashiCorp Vault, AWS KMS, Azure Key Vault) ' +
          'for HSM-backed envelope encryption. When set, each token gets a unique data encryption key ' +
          'from the KMS. When blank, uses the platform\'s built-in AES-256-GCM with local master key.',
      },
    ],
    outputSchema: {
      action: 'string',
      token: 'string',
      category: 'string',
      resolved: 'boolean',
      keyProvider: 'string',
    },
    tags: ['vault', 'tokenize', 'encrypt', 'aes-256', 'data-protection', 'kms', 'envelope-encryption'],
  },

  vault_resolve: {
    type: 'vault_resolve',
    /* RC-98e — alias of `token_vault` (action=resolve). Canonical entry
     * carries the schema. Hidden to avoid palette duplication; operators
     * select the action via the token_vault `action` field. */
    paletteHidden: true,
    label: 'Vault Resolve (Detokenize)',
    category: 'enforcement',
    icon: 'KeyRound',
    description: 'Resolves a vault token back to its original sensitive value. Alias for token_vault with action=resolve.',
    color: '#dc2626',
    aliasOf: 'token_vault',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [],
    outputSchema: {},
    tags: ['vault', 'resolve', 'detokenize', 'alias'],
  },

  // ── Audit Record (Hash-Chained) ────────────────────────────────────────
  audit_event: {
    type: 'audit_event',
    /* RC-98b — unhidden. configSchema (7 fields) is production-ready and
     * BE handler DAGNodeActivities.executeAuditRecord() is fully wired
     * (writes via policyAuditService with tamper-evident hash chain;
     * non-blocking on failure). Was incorrectly tagged "not production-
     * ready" — it was complete. */
    label: 'Audit Record',
    category: 'enforcement',
    icon: 'FileCheck',
    description:
      'Appends a tamper-evident audit record to the hash-chained audit trail (SHA-256). ' +
      'Non-blocking — audit failures do not stop workflow execution. ' +
      'Supports GDPR Art. 30, HIPAA §164.312, SOC 2 compliance.',
    color: '#dc2626',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'eventType',
        label: 'Event Type',
        type: 'select',
        required: true,
        options: [
          'POLICY_RULE_EVALUATION',
          'DATA_CLASSIFICATION',
          'WORKFLOW_ENTRY_GATE',
          'INTER_STEP_EVALUATION',
          'CONNECTOR_GUARD',
          'COMPLIANCE_SCORING',
          'ENFORCEMENT_EXTRACTION',
          'INFERENCE_CALL',
          'APPROVAL_EMAIL_SENT',
          'INVITATION_EMAIL_SENT',
          'HEALTH_ALERT_SENT',
          'POLICY_VIOLATION_ALERT',
          'DIGEST_REPORT_SENT',
        ],
        default: 'POLICY_RULE_EVALUATION',
        helpText: 'Type of enforcement event being recorded',
      },
      {
        field: 'decision',
        label: 'Decision',
        type: 'select',
        required: false,
        options: ['ALLOW', 'BLOCK', 'WARN'],
        default: 'ALLOW',
        helpText: 'Enforcement decision outcome',
      },
      {
        field: 'tenantId',
        label: 'Tenant ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.tenantId}',
      },
      {
        field: 'orgId',
        label: 'Org ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.orgId}',
      },
      {
        field: 'agentId',
        label: 'Agent ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.agentId}',
      },
      {
        field: 'userId',
        label: 'User ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.userId}',
      },
      {
        field: 'workflowExecutionId',
        label: 'Workflow Execution ID',
        type: 'text',
        required: false,
        placeholder: '${trigger-1_output.workflowExecutionId}',
      },
    ],
    outputSchema: {
      eventId: 'string',
      eventType: 'string',
      eventHash: 'string',
      previousEventHash: 'string',
      decision: 'string',
      recorded: 'boolean',
    },
    tags: ['audit', 'compliance', 'hash-chain', 'gdpr', 'hipaa', 'soc2'],
  },

  audit_record: {
    type: 'audit_record',
    /* RC-98e — alias of `audit_event`. BE handler routes both to
     * DAGNodeActivitiesImpl.executeAuditRecord. Canonical entry carries
     * the schema; hidden to avoid palette duplication. */
    paletteHidden: true,
    label: 'Audit Record',
    category: 'enforcement',
    icon: 'FileCheck',
    description: 'Alias for audit_event — hash-chained audit trail entry.',
    color: '#dc2626',
    aliasOf: 'audit_event',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [],
    outputSchema: {},
    tags: ['audit', 'alias'],
  },

  policy_audit: {
    type: 'policy_audit',
    /* RC-98e — alias of `audit_event`. BE handler routes both to
     * DAGNodeActivitiesImpl.executeAuditRecord. Canonical entry carries
     * the schema; hidden to avoid palette duplication. */
    paletteHidden: true,
    label: 'Policy Audit',
    category: 'enforcement',
    icon: 'FileCheck',
    description: 'Alias for audit_event — policy-specific audit trail entry.',
    color: '#dc2626',
    aliasOf: 'audit_event',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [],
    outputSchema: {},
    tags: ['audit', 'policy', 'alias'],
  },

  // ── Compliance Scoring ─────────────────────────────────────────────────
  compliance_scoring: {
    type: 'compliance_scoring',
    /* RC-98b — unhidden. configSchema (2 fields: agentId+tenantId) is
     * production-ready and BE handler DAGNodeActivities.executeCompliance
     * Scoring() is fully wired (calls complianceScoreService.calculateScore
     * and returns rich autonomy/evals/violation output). Was incorrectly
     * tagged "not production-ready". */
    label: 'Compliance Scoring',
    category: 'enforcement',
    icon: 'Award',
    description:
      'Calculates rolling 30-day compliance score for an agent. Determines autonomy level ' +
      '(SELF_AUTONOMOUS / SUPERVISED / RESTRICTED / PROBATION) based on weighted violation history.',
    color: '#dc2626',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'agentId',
        label: 'Agent ID',
        type: 'text',
        required: true,
        placeholder: '${trigger-1_output.agentId}',
        helpText: 'Agent to calculate compliance score for',
      },
      {
        field: 'tenantId',
        label: 'Tenant ID',
        type: 'text',
        required: true,
        placeholder: '${trigger-1_output.tenantId}',
        helpText: 'Tenant scope for the score calculation',
      },
    ],
    outputSchema: {
      agentId: 'string',
      score: 'number',
      recommendedAutonomy: 'string',
      totalEvaluations: 'number',
      blockCount: 'number',
      warnCount: 'number',
      auditCount: 'number',
      weightedViolations: 'number',
      windowStart: 'string',
      windowEnd: 'string',
      calculatedAt: 'string',
    },
    tags: ['compliance', 'scoring', 'autonomy', 'agent'],
  },

  compliance_score: {
    type: 'compliance_score',
    /* RC-98e — alias of `compliance_scoring` (legacy short name).
     * Canonical entry carries the schema; hidden to avoid palette
     * duplication. */
    paletteHidden: true,
    label: 'Compliance Score',
    category: 'enforcement',
    icon: 'Award',
    description: 'Alias for compliance_scoring.',
    color: '#dc2626',
    aliasOf: 'compliance_scoring',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [],
    outputSchema: {},
    tags: ['compliance', 'alias'],
  },

  // ── External Workflow Execution ──────────────────────────────────
  execute_external: {
    type: 'execute_external',
    /* RC-98c — modernized configSchema from legacy {key, description}
     * style to the production {field, label, type, required, placeholder,
     * helpText} style used by dpg_scan / policy_rule_evaluation etc.
     *
     * Ground truth: CanvasDAGWorkflowImpl line 1711 reads ONLY two fields
     * from nodeConfig — `connectorConfigId` and `workflowType`. The old
     * schema also asked for `connectorType` and `host`, but BE IGNORES
     * those — they're resolved server-side from the WorkflowConnectorConfig
     * row keyed by connectorConfigId. Asking operators for them was
     * misleading. Dropped.
     *
     * Output keys come from DAGNodeActivitiesImpl.executeExternalWorkflow
     * line 739 — externalWorkflowId, externalRunId, externalStatus,
     * connectorType, workflowType, host, polled. */
    label: 'Execute External Workflow',
    category: 'external',
    icon: 'ExternalLink',
    description: 'Executes a workflow on an external orchestrator (Temporal, Conductor, Argo, Step Functions, Airflow, Prefect) via the WorkflowConnector SPI. Used in GEL enforcement DAGs after enforcement gates pass.',
    color: '#0891b2',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [
      {
        field: 'connectorConfigId',
        label: 'Workflow Connector Config ID',
        type: 'text',
        required: true,
        placeholder: '${trigger-1_output.workflowConnectorConfigId}',
        helpText: 'UUID of a configured WorkflowConnectorConfig row (admin-provisioned). The connectorType (TEMPORAL / CONDUCTOR / ARGO / STEP_FUNCTIONS / AIRFLOW / PREFECT / CUSTOM), host, port, auth, and TLS settings are all resolved server-side from this row — do not duplicate them here.',
      },
      {
        field: 'workflowType',
        label: 'External Workflow Type',
        type: 'text',
        required: true,
        placeholder: 'order_fulfillment_v2',
        helpText: 'Canonical workflow name on the external orchestrator (e.g., a Conductor workflow definition name, a Temporal WorkflowType, an Argo Workflow template name).',
      },
    ],
    outputSchema: {
      externalWorkflowId: 'string',
      externalRunId: 'string',
      externalStatus: 'string',
      connectorType: 'string',
      workflowType: 'string',
      host: 'string',
      polled: 'boolean',
    },
    tags: ['external', 'workflow', 'connector', 'conductor', 'temporal', 'argo', 'airflow', 'gel'],
  },

  external_workflow: {
    type: 'external_workflow',
    /* RC-98e — alias of `execute_external`. BE workflow handler accepts
     * both type strings (CanvasDAGWorkflowImpl line 1711:
     * `case "execute_external": case "external_workflow":`). Canonical
     * entry carries the modernized schema; hidden to avoid palette
     * duplication. */
    paletteHidden: true,
    label: 'External Workflow',
    category: 'external',
    icon: 'ExternalLink',
    description: 'Alias for execute_external.',
    color: '#0891b2',
    aliasOf: 'execute_external',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [{ name: 'default', label: 'Out' }],
    },
    configSchema: [],
    outputSchema: {},
    tags: ['external', 'workflow', 'alias'],
  },

  /* ── Auto-inserted enforcement nodes ──────────────────────────────────
   *
   * These two are created by services/enforcementAutoInsertion.js when an
   * operator clicks Protect, and until now they existed ONLY there. Being
   * absent from this registry meant getNodePorts() returned {in:[],out:[]}
   * for them, so useCanvas.getNodePortConfig fell through to its generic
   * fallback and the injector could not ask what ports to wire — which is
   * why it hardcoded them, and hardcoded them wrongly.
   *
   * paletteHidden because the platform inserts them; an operator does not
   * drag them in. Hidden from the palette is not the same as unknown to the
   * product, and the difference is what this fixes.
   */

  agent_autonomy_gate: {
    type: 'agent_autonomy_gate',
    paletteHidden: true,
    label: 'Autonomy Gate',
    category: 'enforcement',
    icon: 'ShieldCheck',
    description:
      "Checks the agent's trust level before an autonomous action runs, and holds the run for approval when it falls short.",
    color: '#dc2626',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'default', label: 'Allowed', color: '#22c55e' },
        { name: 'blocked', label: 'Blocked', color: '#ef4444' },
      ],
    },
    configSchema: [],
    outputSchema: {},
    tags: ['autonomy', 'gate', 'trust', 'enforcement', 'auto-inserted'],
  },

  dpg_internode_scan: {
    type: 'dpg_internode_scan',
    paletteHidden: true,
    label: 'DPG Inter-Node Scan',
    category: 'enforcement',
    icon: 'ScanSearch',
    description:
      'Classifies data passing between two steps and records where it went, without the overhead of tokenizing it.',
    color: '#dc2626',
    ports: {
      in: [{ name: 'default', label: 'In' }],
      out: [
        { name: 'default', label: 'Allowed', color: '#22c55e' },
        { name: 'blocked', label: 'Blocked', color: '#ef4444' },
      ],
    },
    configSchema: [],
    outputSchema: {},
    tags: ['dpg', 'scan', 'lineage', 'enforcement', 'auto-inserted'],
  },
};

// ============================================================================
// COMBINE ALL NODE TYPES
// ============================================================================

export const NODE_TYPES = {
  ...TRIGGER_NODES,
  ...TASK_NODES,
  ...SECURITY_OPS_NODES,
  ...LOGIC_NODES,
  ...GATE_NODES,
  ...DATA_NODES,
  ...AGENT_NODES,
  ...ENFORCEMENT_NODES,
  ...CONNECTOR_NODES,
};

// ============================================================================
// RC-100 — buildPickerGroups: AUTO-DERIVE TYPE_GROUPS FROM NODE_TYPES
// ============================================================================
//
// Iterates every NODE_TYPES entry once and routes it into exactly one
// PICKER_BUCKETS bucket. Routing precedence:
//   1. explicit typeIds match  (used for the `tasks` category split)
//   2. categories match        (covers everything else 1:1)
//
// Throws on:
//   - orphan node: NODE_TYPES entry that matches zero buckets
//   - double routing: NODE_TYPES entry that matches >1 bucket
//   - unknown typeIds: bucket references an id not in NODE_TYPES
//     (catches phantoms like the pre-RC-100 `form_task`)
//
// The throws give a precise repro of the drift so future-you knows
// exactly what to fix.

function buildPickerGroups(nodeTypes, buckets) {
  const orphans = [];
  const doubles = [];
  const phantomIds = [];

  // Index of all known node type ids (for phantom detection)
  const knownIds = new Set(Object.keys(nodeTypes));

  // Phantom check FIRST — any typeId in a bucket that doesn't exist
  // as a NODE_TYPES key is a phantom (the form_task case pre-RC-100).
  for (const b of buckets) {
    if (!Array.isArray(b.typeIds)) continue;
    for (const id of b.typeIds) {
      if (!knownIds.has(id)) phantomIds.push({ bucket: b.id, id });
    }
  }

  // Group nodes by bucket via the routing rules
  const grouped = new Map(buckets.map((b) => [b.id, []]));

  for (const [typeId, node] of Object.entries(nodeTypes)) {
    if (!node || !node.category) continue; // skip malformed entries

    const matches = [];
    for (const b of buckets) {
      // Explicit typeIds win
      if (Array.isArray(b.typeIds) && b.typeIds.includes(typeId)) {
        matches.push(b.id);
        continue;
      }
      // Else category match — but skip if a bucket with explicit
      // typeIds for THIS category already claimed it. Concretely:
      // tasks-category nodes that aren't in tasks-work / messaging /
      // database typeIds would otherwise fall through to no bucket.
      // We handle that as an orphan, not silent drop.
      if (Array.isArray(b.categories) && b.categories.includes(node.category)) {
        // Skip if this node is in a SIBLING bucket's typeIds — avoids
        // double-routing for the tasks-category split.
        const claimedElsewhere = buckets.some(
          (other) => other.id !== b.id
            && Array.isArray(other.typeIds)
            && other.typeIds.includes(typeId),
        );
        if (claimedElsewhere) continue;
        matches.push(b.id);
      }
    }

    if (matches.length === 0) orphans.push({ typeId, category: node.category });
    else if (matches.length > 1) doubles.push({ typeId, category: node.category, matches });
    else grouped.get(matches[0]).push(typeId);
  }

  if (phantomIds.length > 0 || orphans.length > 0 || doubles.length > 0) {
    const parts = [];
    if (phantomIds.length > 0) {
      parts.push(`Phantom typeIds in PICKER_BUCKETS (not in NODE_TYPES): ${
        phantomIds.map((p) => `${p.id} (in bucket ${p.bucket})`).join(', ')
      }`);
    }
    if (orphans.length > 0) {
      parts.push(`Orphan NODE_TYPES (no PICKER_BUCKETS matched): ${
        orphans.map((o) => `${o.typeId} [category=${o.category}]`).join(', ')
      }`);
    }
    if (doubles.length > 0) {
      parts.push(`Double-routed NODE_TYPES: ${
        doubles.map((d) => `${d.typeId} → ${d.matches.join('+')}`).join(', ')
      }`);
    }
    const msg = `[soarNodes/RC-100] PICKER_BUCKETS ↔ NODE_TYPES drift detected:\n  - ${parts.join('\n  - ')}\nFix in soarNodes.js: either widen the relevant bucket's categories/typeIds or correct the node's category field.`;
    // Hard-fail in dev/test so regressions are caught immediately.
    // In production, log loudly but don't crash the canvas — the
    // worst-case fallback is the picker missing a few entries, which
    // is preferable to a blank screen.
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
      throw new Error(msg);
    } else if (typeof console !== 'undefined') {
      console.error(msg);
    }
  }

  // Build the final TYPE_GROUPS shape the picker consumer expects:
  // [{ label, hint, types: [{id, hint, isAlias?}, ...] }, ...]
  // Hint per type defaults to a one-line slice of the node's
  // description; consumers can supply a richer label-level hint via
  // PICKER_BUCKETS.hint at the bucket level.
  return buckets.map((b) => {
    const ids = grouped.get(b.id) || [];
    const types = ids.map((id) => {
      const node = nodeTypes[id];
      const isAlias = !!(node && node.aliasOf);
      // Prefer an explicit short hint if the node sets `pickerHint`;
      // otherwise use the first ~80 chars of description as a graceful
      // fallback. Operators get a meaningful hint either way.
      const explicitHint = node && typeof node.pickerHint === 'string' ? node.pickerHint.trim() : '';
      const descHint = node && typeof node.description === 'string'
        ? node.description.split(/[.\n]/)[0].trim().slice(0, 100)
        : '';
      return {
        id,
        hint: explicitHint || descHint || '',
        ...(isAlias ? { isAlias: true } : {}),
      };
    });
    return { id: b.id, label: b.label, hint: b.hint, types };
  });
}

/**
 * RC-100 — Single-source-of-truth TYPE_GROUPS for the Change-Type picker.
 *
 * Built from NODE_TYPES + PICKER_BUCKETS at module-init. If you add a
 * new node type to NODE_TYPES, it auto-appears in the picker via its
 * category match — no separate TYPE_GROUPS edit needed. If your new
 * node's category isn't covered by any PICKER_BUCKETS entry, the
 * validator throws with a clear message at import time so the build
 * fails loudly rather than silently dropping the node from the picker.
 *
 * Consumer (NodeTypeSwapControl.jsx) is unchanged — same exported shape.
 */
export const TYPE_GROUPS = buildPickerGroups(NODE_TYPES, PICKER_BUCKETS);

// ============================================================================
// FLAT NODE TYPE MAP FOR QUICK LOOKUP
// ============================================================================

export const NODE_TYPE_MAP = NODE_TYPES;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Legacy / variant → canonical type map. When a workflow is loaded with
 * an older or non-canonical node type string, we still want the right
 * configSchema, ports, icon, etc. to render — otherwise the right-panel
 * config tab falls through to "No configuration available" and the
 * operator can't reach installed connectors / actions / params.
 *
 * Cause this matters: workflows authored at different points in the
 * platform's history persist their `type` field with whatever the
 * conventions were at the time. The renderer must be backwards-
 * compatible across all of them, not just today's canonical names.
 */
const TYPE_ALIASES = {
  // Approval / human-in-the-loop renamed family
  approval_gate: 'human_task',
  approval:      'human_task',
  // Connector node — both the short alias and the platform's
  // capital-case variant land on the same canonical definition.
  connector:           'connector_task',
  CONNECTOR_TASK:      'connector_task',
  CONNECTOR:           'connector_task',
  // Common case-variants we've seen on persisted workflows
  HUMAN_TASK:    'human_task',
  SCRIPT_TASK:   'script_task',
  DATA_TRANSFORM:'data_transform',
  LLM_TASK:      'llm_task',
  SWITCH:        'switch',
  CONDITION:     'condition',

  // RC-AIPrompt-PhantomTypes — the BE AI prompt (CanvasAIService.java)
  // teaches Claude to emit these types, but they have no NODE_TYPES
  // entry. Without an alias, getNodeTypeDef returns null and the
  // canvas falls back to UnsupportedEditor + the palette can't
  // highlight on selection. The BE either routes them to the same
  // handler as the canonical (variable_assignment + data_enrichment
  // → data_transform per CanvasDAGWorkflowImpl switch) or they're
  // specialised SOAR actions that share the connector_task schema.
  //
  // Long-term: either give each a proper NODE_TYPES entry (preferred
  // when the config differs meaningfully) or remove from the AI
  // prompt. Until then, these aliases unblock the operator workflow.
  variable_assignment: 'data_transform',
  data_enrichment:     'data_transform',
  cognitive_loop:      'loop',
  risk_scoring:        'risk_score_gate',
  forensics_action:    'connector_task',
  isolation_action:    'connector_task',
  remediation_action:  'connector_task',

  // RC-AIPrompt-PhantomTypes-Round2 — stub-types that bled in via
  // canvasRecipes.js / insertionHints.js stub recipes (legacy
  // unbuilt-feature placeholders that the AI scrapes into context):
  //   • policy_gate → policy_enforcement_gate (the canonical
  //     governance gate; same multi-stage evaluator behind both
  //     names, only the short alias was operator-facing).
  //   • output_validator → policy_rule_evaluation (closest semantic
  //     match — both evaluate the upstream output against a rule
  //     set; original output_validator never shipped a dedicated BE
  //     handler. Operator who wants real schema validation should
  //     use connector_task with a schema-validation skill).
  policy_gate:         'policy_enforcement_gate',
  output_validator:    'policy_rule_evaluation',
  // Short-name aliases for canonical types already in NODE_TYPES —
  // recipes and persisted workflows historically used the short
  // forms; this keeps both stable while the canonical drives the
  // palette / inspector.
  'switch':            'switch_case',
  audit_log:           'audit_record',
};

/**
 * Get full node type definition by type ID. Tolerant of:
 *   - Exact registry keys (canonical path)
 *   - Legacy / renamed types via {@link TYPE_ALIASES}
 *   - Case variations (some persisted workflows store types as UPPERCASE)
 *   - Marketplace-templated connector types (connector_<vendor>_<ver>) that
 *     haven't been registered yet because the catalogue fetch is slow or
 *     the underlying connector manifest is missing — these still render
 *     using the base connector_task schema so the operator can configure
 *     connectorId/action/parameters even when the rich metadata isn't
 *     available.
 *
 * Returns null only when no plausible match exists.
 *
 * @param {string} type - Node type ID
 * @returns {object|null} Node type definition or null
 */
export function getNodeTypeDef(type) {
  if (type == null) return null;
  // 1. Exact match (canonical)
  if (NODE_TYPES[type]) return NODE_TYPES[type];
  // 2. Explicit alias — handles 'connector', uppercase 'CONNECTOR_TASK', etc.
  const aliased = TYPE_ALIASES[type];
  if (aliased && NODE_TYPES[aliased]) return NODE_TYPES[aliased];
  // 3. Case-fold fallback — last-ditch defence so a stray 'Connector_Task'
  //    or 'connector_TASK' still resolves rather than ghosting the panel.
  const lc = String(type).toLowerCase();
  if (NODE_TYPES[lc]) return NODE_TYPES[lc];
  const lcAlias = TYPE_ALIASES[lc];
  if (lcAlias && NODE_TYPES[lcAlias]) return NODE_TYPES[lcAlias];
  // 4. Marketplace-templated connector fallback. The palette registers
  //    each connector as `connector_<vendor>_<version>` (see
  //    {@link buildConnectorNode}); these definitions only exist AFTER
  //    fetchMarketplaceCatalogue() has resolved. Until then — or
  //    permanently for nodes whose underlying manifest has been retired —
  //    we synthesise a definition from the base connector_task so the
  //    config tab renders connectorId/action/parameters. The operator
  //    can still pick an installed connector and an action; only the
  //    rich vendor-specific metadata (icon colour, skill list, docs link)
  //    is missing, and that's recoverable as soon as the catalogue loads.
  if (lc.startsWith('connector_') && NODE_TYPES.connector_task) {
    const base = NODE_TYPES.connector_task;
    return {
      ...base,
      type,                       // preserve the workflow's actual stored type
      label: base.label,
      _synthesizedFrom: 'connector_task',
    };
  }
  return null;
}

/**
 * Get port definitions for a given node type
 * @param {string} type - Node type ID
 * @returns {object} Port definitions {in, out}
 */
export function getNodePorts(type) {
  const nodeDef = getNodeTypeDef(type);
  return nodeDef ? nodeDef.ports : { in: [], out: [] };
}

/**
 * Get all node types in a specific category
 * @param {string} categoryId - Category ID
 * @returns {array} Array of node type definitions
 */
export function getCategoryNodes(categoryId) {
  return Object.values(NODE_TYPES).filter((node) => node.category === categoryId && !node.paletteHidden);
}

/**
 * Search nodes by label, type, description, or tags
 * @param {string} query - Search query
 * @returns {array} Matching node type definitions
 */
export function searchNodes(query) {
  const lowerQuery = query.toLowerCase();

  return Object.values(NODE_TYPES).filter((node) => {
    if (node.paletteHidden) return false;
    const matchLabel = node.label.toLowerCase().includes(lowerQuery);
    const matchType = node.type.toLowerCase().includes(lowerQuery);
    const matchDescription = node.description.toLowerCase().includes(lowerQuery);
    const matchTags = node.tags.some((tag) => tag.toLowerCase().includes(lowerQuery));
    const matchCategory = node.category.toLowerCase().includes(lowerQuery);

    return matchLabel || matchType || matchDescription || matchTags || matchCategory;
  });
}

/**
 * Get category definition by ID
 * @param {string} categoryId - Category ID
 * @returns {object} Category definition or null
 */
export function getCategoryDef(categoryId) {
  return NODE_CATEGORIES.find((cat) => cat.id === categoryId) || null;
}

/**
 * Validate node configuration against schema
 * @param {string} nodeType - Node type ID
 * @param {object} config - Configuration object
 * @returns {object} {valid: boolean, errors: array}
 */
export function validateNodeConfig(nodeType, config) {
  const nodeDef = getNodeTypeDef(nodeType);
  if (!nodeDef) {
    return { valid: false, errors: ['Unknown node type'] };
  }

  const errors = [];
  const schema = nodeDef.configSchema || [];

  for (const field of schema) {
    if (field.required && !(field.field in config)) {
      errors.push(`Missing required field: ${field.label}`);
    }

    if (field.field in config) {
      const value = config[field.field];

      // Type validation
      if (field.type === 'number' && typeof value !== 'number') {
        errors.push(`${field.label} must be a number`);
      }
      if (field.type === 'boolean' && typeof value !== 'boolean') {
        errors.push(`${field.label} must be a boolean`);
      }

      // Min/max validation
      if (field.type === 'number' && field.min !== undefined && value < field.min) {
        errors.push(`${field.label} must be >= ${field.min}`);
      }
      if (field.type === 'number' && field.max !== undefined && value > field.max) {
        errors.push(`${field.label} must be <= ${field.max}`);
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Get all nodes with specific tag
 * @param {string} tag - Tag to filter by
 * @returns {array} Matching node type definitions
 */
export function getNodesByTag(tag) {
  return Object.values(NODE_TYPES).filter((node) => node.tags.includes(tag));
}

export default NODE_TYPES;
